import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import LoadingSpinner from '../components/LoadingSpinner';
import ErrorMessage from '../components/ErrorMessage';
import { useVatMode } from '../hooks/useVatMode';
import useSubmitGuard from '../hooks/useSubmitGuard';
import useDirtyContinuation from '../hooks/useDirtyContinuation';
import useExpandedOrderCore from '../hooks/useExpandedOrderCore';
import { apiFetch, apiGet, apiPost, apiThrow, API_BASE } from '../utils/api';
import { loadAccountingLists, loadOrderReferenceLists } from '../utils/orderReferenceLists';
import { orderMatchesSearch } from '../utils/orderSearch';
import { CSV_EXT, CSV_LABEL, CSV_MENU_LABEL } from '../utils/gagCsvExport';
import { compareOrderRows, applySortClick, orderInitialSortDir } from '../utils/tableSort';
import { toLocalISO } from '../utils/dateFormat';
import { approveButtonLabel } from '../utils/approvalStepCollapse';
import ConfirmDialog from '../components/ConfirmDialog';
import {
  SAVE_THEN_ACT_TITLE,
  SAVE_THEN_ACT_KEEP_EDITING,
  saveThenActCopy,
} from '../constants/saveThenActPrompt';
import { useDepartment } from '../contexts/DepartmentContext';
import { useOrderAuthority } from '../hooks/useOrderAuthority';
// 🔻 `URGENCY_LABELS` / `URGENCY_PRIORITY` came off this import on 2026-08-09
// with the `Sort: Urgency` reducer — see `components/OrderSearchAndSort.js`.
// `URGENCY_LABELS` is now DELETED entirely (2026-08-18, with `UrgencyBadge.js`).
import EmptyState from '../components/EmptyState';
// 🆕 The actions menu (2026-08-10). `OrderPdfMenuItems` is a NEW capability on
// this page — it never had an `OrderPdfButton` — see the note at the menu.
import OrderActionsMenu, { MENU_ITEM_NEUTRAL as ORDER_ACTIONS_MENU_ITEM } from '../components/OrderActionsMenu';
import PhoneDecisionBar from '../components/ui/PhoneDecisionBar';
import OrderPdfMenuItems from '../components/OrderPdfMenuItems';
import OrderListPrintMenuItem from '../components/OrderListPrintMenuItem';
import { exportSingleOrderCsv } from '../utils/exportSingleOrder';
import OrderInlineEditor from '../components/OrderInlineEditor';
import OrderExpandedDetails from '../components/OrderExpandedDetails';
// 🔀 (2026-08-17) — the READ-ONLY branch's hand-rolled panel is replaced by
// this shared component, closing the divergence from Dept Orders / My
// Orders. The EDIT branch keeps its own direct `OrderInlineEditor` call
// below, unchanged — see the docblock at `readOnlyExpandedContent`.
import OrderExpandedRow from '../components/OrderExpandedRow';
import {
  OrderTableShell,
  OrderTableRow,
  OrderMobileTableRow,
  ORDER_TABLE_COLUMNS,
  ORDER_COL_WIDTHS_KEY,
  APPROVALS_MOBILE_TABLE_COLUMNS,
  APPROVALS_MOBILE_COL_WIDTHS_KEY,
  MAX_ORDER_COLUMN_WIDTH,
  // Task 6 (2026-08-31), Fix Round 1 Finding 1 — the mode decision lives HERE,
  // on the page, via the existing `columns` PROP. See that export's docblock
  // in `OrderTableView.js` for why this moved out of the shell.
} from '../components/OrderTableView';
import useColumnWidths from '../hooks/useColumnWidths';
import useFitColumnsToScreen from '../hooks/useFitColumnsToScreen';
import Dropdown from '../components/ui/Dropdown';
import { useIsMobileOrderViewport } from '../hooks/useIsMobileOrderViewport';
// 🔀 (2026-08-17) — `InlinePoNumberEditor` / `InlineLineItemSelect` /
// `OrderItemActionsMenu` / `OrderLineItemFlag` / `FLAGGED_ROW_CLASS` /
// `isLineFlagged` were used ONLY by the hand-rolled read-only panel this
// migration deletes (`OrderExpandedRow` renders the equivalent controls
// itself, through `OrderForm`). Removed rather than left dead — the EDIT
// branch below never used any of them.
import OrderSearchAndSort from '../components/OrderSearchAndSort';
import MobileTabSearchRow, { PHONE_HEADER_TOOLBAR } from '../components/MobileTabSearchRow';
import Toolbar, { TOOLBAR_DISCLOSURE_TRIGGER } from '../components/ui/Toolbar';
import PageHeaderBand from '../components/ui/PageHeaderBand';
import useStickyToolbar from '../hooks/useStickyToolbar';
import usePollingRefresh from '../hooks/usePollingRefresh';
import useDeepLinkParam from '../hooks/useDeepLinkParam';
import { scrollUnderPinnedChrome } from '../hooks/useOpenAtTop';
import { useToastOrNoop } from '../contexts/ToastContext';
import useListRefreshWhenClean from '../hooks/useListRefreshWhenClean';
import useUnsavedChangesGuard, { UNSAVED_ORDER_CHANGES_MESSAGE } from '../hooks/useUnsavedChangesGuard';
import { refreshOneOrder } from '../utils/refreshOneOrder';
import { withDirtyRowPinned } from '../utils/withDirtyRowPinned';
import { getOrderNumber } from '../utils/orderTitle';
import InternalNotesReadOnly from '../components/InternalNotesReadOnly';

// The empty-reason warning — one string, so `clearRejectReasonError` can tell it
// from a real failure sharing the same `error` slot.
const REJECT_REASON_REQUIRED = 'Please provide a reason for rejection';

function Approvals() {
  const navigate = useNavigate();
  const { formatMoney } = useVatMode();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [actionLoading, setActionLoading] = useState({});
  const [rejectNotes, setRejectNotes] = useState({});
  const [showRejectForm, setShowRejectForm] = useState({});
  const [successMessage, setSuccessMessage] = useState('');
  // Expandable row state + every write to the detail cache, shared with the two
  // order-expansion hooks since 2026-10-03. 🔑 `useExpandedOrderCore` runs NO
  // effect, so calling it here cannot move this page's mount request order —
  // see its docblock for why that is the load-bearing property.
  const {
    expandedOrders, setExpandedOrders,
    orderDetails, setOrderDetails,
    detailsLoading, setDetailsLoading,
    updateOrderListFromItem,
    handleCostCodeChange,
    handlePaymentTypeChange,
    handleItemNotifyUpdated,
    handlePoNumberSaved,
    handleExpectedDeliverySaved,
  } = useExpandedOrderCore({ setOrders });
  const [costCodes, setCostCodes] = useState([]);
  const [paymentTypes, setPaymentTypes] = useState([]);
  // Reference data for the expand-to-edit form. Fetched ONCE PER PAGE (see the
  // effect below), never per expanded row — the twin of DepartmentOrders.js.
  const [productions, setProductions] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [subDepartments, setSubDepartments] = useState([]);
  const [gags, setGags] = useState([]);
  // 🚨 WHICH REFERENCE LISTS ARE EMPTY BECAUSE THE REQUEST FAILED — 2026-08-22.
  //
  // `[]` is the shape of "this department has none" AND of "nobody asked", and
  // `components/OrderForm.js` makes confident claims from it in the inline
  // editor: *"No suppliers set up for this department yet"* (worded to send the
  // reader off to CREATE supplier records), and — via an emptied option list —
  // every coded line silently redrawn as `-- None --`. The failure happens
  // here and the claim is rendered there. See `OrderForm`'s `loadFailures`.
  //
  // ⚠️ This is a DIFFERENT thing from `assignmentsError` further down, which
  // comes from the context and covers the membership question. Both are
  // "we could not ask"; they cover different questions and neither implies
  // the other.
  const [loadFailures, setLoadFailures] = useState({});
  // Expand-to-edit dirty guard. `editDirtyId` holds the id of the row whose
  // inline form has unsaved changes — an ID, not a boolean, so a closing
  // panel's unmount cleanup (which reports `false`) can only clear ITS OWN flag
  // and never a freshly-expanded row's.
  //
  // 🔑 AN ID AND NOT A SET, AND THAT IS A PROPERTY OF THE PAGE, NOT AN
  // OVERSIGHT. This page's `toggleExpand` is an accordion (see its docblock),
  // so at most one editor exists at any moment.
  // A `Set` that can only ever hold one element advertises a case that cannot
  // occur, and this repo has been bitten by shapes implying capabilities they
  // do not have.
  const [editDirtyId, setEditDirtyId] = useState(null);
  // 🔀 2026-09-28 — THE ONE CONTINUATION GUARD (`hooks/useDirtyContinuation.js`),
  // shared with Department Orders and My Orders: every gesture that would lose
  // the dirty edit opens the editor's three-way prompt (`promptSignal`) and
  // then does what was clicked. Replaced this page's `blockIfDirty` + pulse.
  const {
    continueWhenClean,
    promptSignal: collapseRequestSignal,
    onPromptResolved,
  } = useDirtyContinuation({ dirty: editDirtyId !== null });
  const toggleExpandRef = useRef(null);
  // ── THE UNSAVED-CHANGES PROMPT IN FRONT OF SAVE-THEN-ACT (2026-08-10) ────
  // The twin of Department Orders'. `{ orderId, kind, run }` while the dialog is
  // open, else null — `run` is the SAME closure the click built, held rather
  // than rebuilt, so confirming does exactly what the button did yesterday.
  //
  // 🔑 An OBJECT and not three pieces of state: `useState` on a bare function
  // would CALL it (the functional-update form), and the three can only ever be
  // set or cleared together.
  //
  // ⚠️ ONE at a time is safe here for the reason recorded above `editDirtyId`:
  // the TABLE layout on this page is a single-expand accordion, so at most one
  // form exists and at most one can be dirty.
  const [pendingSaveAction, setPendingSaveAction] = useState(null);
  // The open inline editor's save handle — `() => Promise<boolean>`, filled by
  // `OrderInlineEditor` and resolving `true` only after a `res.ok` PUT.
  //
  // 🔑 ONE ref, not a map keyed by row id, and that is a property of the page
  // rather than a shortcut: the editor renders in the TABLE layout only and
  // `toggleExpand` is an accordion there, so at most one editor exists at any
  // moment — the same argument that makes `editDirtyId` an id and not a `Set`.
  const editorSaveRef = useRef(null);
  // Set for the window between "a row action asked for a save" and that action
  // running. `handleEditorSaved` reads it to know the action supersedes its own
  // list refresh — see the comment there.
  const actionAfterSaveRef = useRef(null);
  // ── THE SAVE-EPOCH GUARD (review of the 2026-09-13 upload-race fix) ─────
  // `utils/orderFileMutations.js#refreshOrderFiles` drops a file-upload
  // refresh GET that resolves AFTER a save has landed — see its docblock and
  // `components/OrderExpandedRow.js`'s own `saveEpochRef`. This page's TABLE
  // layout renders `OrderExpandedDetails` (the drop zone that issues that
  // GET) and `OrderInlineEditor` (which bumps the counter on a successful
  // save) DIRECTLY, side by side, instead of through `OrderExpandedRow` — so
  // neither had the ref and the guard did nothing here.
  //
  // 🔑 ONE ref, not a map keyed by row id — same shape and same reason as
  // `editorSaveRef` immediately above: the direct `OrderInlineEditor` only
  // ever renders in the TABLE layout, `toggleExpand` is an accordion there
  // (see its docblock), and `expandedDetails` below is only ever placed
  // alongside that editor (`showInlineEditor` gates both — see
  // `expandedContent`). So at most one order is ever using this ref at a
  // time; a per-row map would advertise a concurrency this page cannot reach.
  // The read-only branch goes through `OrderExpandedRow`
  // instead, which already owns its own `saveEpochRef` — this page's copy is
  // never passed there and never needs to be, because that branch never
  // renders the editor (`showInlineEditor={false}`).
  const saveEpochRef = useRef(0);
  // Sticky toolbar. 🔀 `headerTop` IS used now (2026-08-08): the TABLE
  // layout's column headings are a second-level sticky header and pin just
  // beneath the toolbar. Kept byte-identical to DepartmentOrders.js — the two
  // pages must not pin their headings at different offsets.
  const { toolbarRef, headerTop } = useStickyToolbar();

  // Search + sorting
  const [searchText, setSearchText] = useState('');
  const [sortField, setSortField] = useState('order_date');
  const [sortDir, setSortDir] = useState('desc');

  /* The order cards were retired (phone 2026-10-01, desktop 2026-10-18,
     owner): desktop is always the table; the phone renders
     `APPROVALS_MOBILE_TABLE_COLUMNS` (Dept Orders' phone set MINUS Status)
     through `OrderMobileTableRow`. */
  const isMobile = useIsMobileOrderViewport();
  const isPhoneTable = isMobile;

  /**
   * ⚠️ THIS SITS AHEAD OF THE COLUMN-WIDTHS BLOCK BELOW, AND THE REASON IT WAS
   * MOVED HERE NO LONGER APPLIES. It was moved on 2026-08-31 so that
   * `isAllDepartments` was in scope for a `useMemo` the widths hook consumed;
   * that memo went with the All Productions mode the same day. The position is
   * KEPT rather than moved back because the reorder was inert then and is inert
   * now — no code between the two positions reads any of these fields — and
   * shuffling a destructure for tidiness is churn a reviewer has to re-verify.
   */
  const { isAdmin, isBuyer, activeRole, activeDepartment, assignments, assignmentsError, registerDirtyEditGuard } = useDepartment();
  /**
   * ── User-draggable column widths (2026-08-10) ────────────────────────
   *
   * Product owner: *"to approve and department order tables need the ability to
   * have their column widths adjusted like breakdown."*
   *
   * 🔑 **ONE key, `sfx_order_table_col_widths`, shared with Department Orders —
   * and that is not a shortcut.** Both pages render the SAME twelve columns from
   * the SAME component, so a per-page key would let one component's two callers
   * disagree about one table. Only one order page is mounted at a time, so
   * the two hook instances never race; a cross-TAB change does not sync until
   * reload, exactly as on the Breakdown (this hook has no `storage` listener and
   * must not grow one here — it is shared, and the Breakdown must not regress).
   *
   * The state lives on the page rather than in `OrderTableShell` because
   * `Fit columns to screen` (Department Orders' own `Reset column widths`
   * until 2026-09-28) is an `Actions ▾` item in the header band.
   */
  const {
    widths: columnWidths,
    setWidth: setColumnWidth,
    resetColumn: resetColumnWidth,
  } = useColumnWidths(ORDER_TABLE_COLUMNS, ORDER_COL_WIDTHS_KEY, MAX_ORDER_COLUMN_WIDTH);
  /* 🔑 The PHONE table is a different COLUMN SET → its own width model and
     key (`APPROVALS_MOBILE_COL_WIDTHS_KEY`). Both hooks always called; only
     the active pair reaches the shell. */
  const {
    widths: mobileColumnWidths,
    setWidth: setMobileColumnWidth,
    resetColumn: resetMobileColumnWidth,
  } = useColumnWidths(APPROVALS_MOBILE_TABLE_COLUMNS, APPROVALS_MOBILE_COL_WIDTHS_KEY, MAX_ORDER_COLUMN_WIDTH);
  const activeTableColumns = isPhoneTable ? APPROVALS_MOBILE_TABLE_COLUMNS : ORDER_TABLE_COLUMNS;
  const activeColumnWidths = isPhoneTable ? mobileColumnWidths : columnWidths;
  const activeSetColumnWidth = isPhoneTable ? setMobileColumnWidth : setColumnWidth;
  const activeResetColumnWidth = isPhoneTable ? resetMobileColumnWidth : resetColumnWidth;

  /**
   * `Fit columns to screen` (owner ruling 2026-09-28) — see
   * `DepartmentOrders.js`'s identical block for the full reasoning; both
   * pages share ONE column model, ONE width key and now ONE fit hook call.
   */
  const [tableRegionWidth, setTableRegionWidth] = useState(null);
  const { fitColumnsToScreen, canFitColumns, alreadyFit: columnsAlreadyFit } = useFitColumnsToScreen({
    columns: activeTableColumns,
    widths: activeColumnWidths,
    setWidth: activeSetColumnWidth,
    maxWidth: MAX_ORDER_COLUMN_WIDTH,
    available: tableRegionWidth,
  });

  /**
   * 🔻 **THE `actions` SLOT IS NOW ALWAYS FILLED, and the constant is kept with
   * its reasoning rather than inlined.** `Print all` is ungated, so the menu
   * can never be empty. **`PageHeaderBand` decides with `Boolean(actions)` and
   * a fragment that renders nothing is still truthy**, so the moment somebody
   * gates `Print all` — or it leaves this menu — the slot can go empty again
   * and a stray right-hand flex child appears. Do not "simplify" the slot to an unconditional
   * expression; that is the form that loses the rule.
   */
  const hasHeaderActions = true;

  const currentUser = localStorage.getItem('sfx_requested_by') || '';
  // The viewer's own user_id, for identity comparisons (never compare display names).
  // Same key AuthContext syncs on every session refresh; MyOrders.js reads it the same way.
  const currentUserId = localStorage.getItem('sfx_user_id') || '';
  // What OrderForm calls `userAssignedProductions`: null = admin / still
  // loading, [] = this user holds no assignments at all (the form then blocks
  // submission with an explanation). Computed exactly as OrderPage and
  // DepartmentOrders do it — this is form plumbing, not an authority decision.
  const userAssignedProductions = isAdmin
    ? null
    : (assignments && assignments.length === 0 ? [] : null);
  // Resolved PER ORDER, in the ORDER'S OWN department.
  //
  // 🔻 THE REASON GIVEN HERE EXPIRED ON 2026-08-12 AND THE CODE IS UNCHANGED —
  // read the correction, because the obvious follow-up is wrong. It said:
  // *"This page is the one that most needs it: GET /api/approvals/pending is not
  // department-scoped at all (it selects on approver_user_id), so the list
  // routinely holds orders from departments the viewer is not standing in."*
  // That endpoint IS department-scoped now (product owner: *"only site admin
  // sees more than one department at a time"*), so this page's list no longer
  // spans departments for a non-admin.
  //
  // 🚨 DO NOT "SIMPLIFY" THIS TO THE ACTIVE DEPARTMENT'S `capabilities`. The
  // premise moved; the conclusion did not. `useOrderAuthority` resolves in the
  // ORDER'S OWN department because that is the question THE SERVER ANSWERS
  // (`requireDepartmentCapability(req, res, order.department_id, …)`), and
  // asking a different one on the client is wrong however the list is filtered.
  // It is also still not equivalent in fact: a SITE ADMIN's list does span
  // departments, by the exception above. See hooks/useOrderAuthority.js.
  const { canManageOrder } = useOrderAuthority();
  const currentRole = isAdmin ? 'admin' : (isBuyer ? 'buyer' : (activeRole || 'user'));

  /**
   * @param {object}  [opts]
   * @param {boolean} [opts.silent=false]          suppress the spinner
   * @param {boolean} [opts.suppressErrors=false]  suppress the error banner
   *
   * 🔑 Both flags exist for the 30s poll and mean DIFFERENT things — see the
   * twin on `DepartmentOrders.js`. A refetch the user triggered must report its
   * failure; a background poll must not paint an error over rows they are
   * reading.
   *
   * 🔀 **IT REFETCHES ON A DEPARTMENT SWITCH SINCE 2026-08-12, AND THE
   * DEPENDENCY IS THE WHOLE OF IT.** `GET /api/approvals/pending` is now scoped
   * to the caller's active department, and `utils/api.js` reads that department
   * from `localStorage` at CALL time — so the request is always correct and it is
   * only ever issued at the wrong TIME. Without `activeDepartment` in the deps
   * this page would go on showing the previous department's queue until the 30s
   * poll happened to fire, which is a staleness bug the scoping would have
   * created rather than fixed. **`switchDepartment` is client-side and does not
   * reload the page** (`contexts/DepartmentContext.js`), so nothing else brings
   * the list back.
   *
   * ⚠️ The dep is `activeDepartment?.department_id`, NOT the object. The context
   * hands out a fresh assignment object on every `assignments` refetch, so
   * depending on the object refires this on renders where the department has not
   * changed — and each refire is a full list request plus a spinner.
   */
  const fetchPendingApprovals = useCallback((opts = {}) => {
    const { silent = false, suppressErrors = false } = opts;
    if (!currentUser) {
      setLoading(false);
      return Promise.resolve();
    }
    if (!silent) setLoading(true);
    if (!suppressErrors) setError(null);
    return apiGet(`${API_BASE}/api/approvals/pending`)
      .then(res => {
        if (!res.ok) throw new Error('Failed to fetch pending approvals');
        return res.json();
      })
      .then(data => {
        setOrders(Array.isArray(data) ? data : []);
        if (!silent) setLoading(false);
      })
      .catch(err => {
        if (!suppressErrors) {
          setError(err.message);
          setLoading(false);
        }
        if (suppressErrors) throw err;
      });
  }, [currentUser, currentRole, activeDepartment?.department_id]);

  /**
   * 🔄 THE 30-SECOND BACKGROUND POLL (2026-08-08). Product owner: *"add a 30
   * second poll"*.
   *
   * 🔑 **THIS PAGE ARGUABLY NEEDS IT MOST, which is why it is here and not
   * only on Department Orders.** The To Approve queue is by definition a list
   * of things OTHER PEOPLE have just submitted for you — the one surface where
   * everything that appears comes from someone else's action. An approver
   * sitting on this page is waiting for exactly the rows a poll delivers.
   *
   * 🚨 **`paused` WHILE A REJECT FORM IS OPEN — this page's dirty guard.** It
   * has no inline editor, so at first glance it has nothing to protect; it
   * does. Rejecting requires a typed reason, and `rejectNotes` is unsaved text
   * sitting in a textarea. A refetch that removed that order from the list —
   * because a colleague approved it in the meantime, which is precisely what a
   * poll is for — would unmount the textarea mid-sentence. Skipping ticks while
   * any reject form is open costs at most 30 seconds of staleness and protects
   * the one piece of unsaved work on the page.
   *
   * 🔀 **AND SINCE 2026-08-08 IT IS NO LONGER THE ONLY PIECE OF UNSAVED WORK.**
   * The expand-to-edit form arrived on this page, so `editDirtyId` is a second,
   * far larger body of unsaved typing. The condition is EXTENDED, not replaced —
   * the reject textarea is still just as unsaved as it ever was, and dropping it
   * while adding the editor would fix the new hazard by reintroducing the old
   * one. Ticks are skipped, never queued.
   */
  // ONE pause expression, read by the poll AND by the panel-driven refresh below.
  const listRefreshPaused = Object.values(showRejectForm).some(Boolean) || editDirtyId !== null;
  usePollingRefresh(
    useCallback(
      () => fetchPendingApprovals({ silent: true, suppressErrors: true }),
      [fetchPendingApprovals]
    ),
    { paused: listRefreshPaused }
  );
  // 🔀 2026-09-29 (owner: "fix all") — a panel change the COLLAPSED ROW shows
  // (the attachment paperclip, `file_count`) refetches the list silently;
  // deferred while the poll is paused, never dropped (`hooks/useListRefreshWhenClean.js`).
  const requestListRefresh = useListRefreshWhenClean(
    () => fetchPendingApprovals({ silent: true, suppressErrors: true }).catch(() => {}),
    listRefreshPaused
  );

  useEffect(() => {
    fetchPendingApprovals();
  }, [fetchPendingApprovals]);

  // ── REGISTER THIS PAGE'S DIRTY STATE WITH THE SWITCH GUARD (2026-08-30) ──
  // `DepartmentContext.js`'s `hasDirtyEdit()` is what
  // `ProductionDepartmentSwitcher.js` reads BEFORE calling `switchDepartment`,
  // to decide whether to raise the "you'll lose it" Continue/Cancel dialog.
  // 🔑 **Deliberately `editDirtyId !== null` ALONE, NOT the poll's fuller
  // `paused` expression** (`Object.values(showRejectForm).some(Boolean) ||
  // editDirtyId !== null`, a few lines above). The switch guard exists to
  // protect the same kind of unsaved work the poll's `paused` flag protects on
  // this page, which is keyed on `editDirtyId` too. A
  // typed-but-not-yet-submitted reject reason (`showRejectForm`) is real
  // unsaved text, but the page never treated it as switch-worthy before this
  // change, and widening the guard to include it is a scope decision for the
  // owner, not an inconsistency to "complete" here — flagged, not done.
  // Cleared on unmount so a route change away from this page cannot leave a
  // future switch blocked by an edit that no longer exists.
  useEffect(() => {
    registerDirtyEditGuard?.(editDirtyId !== null);
    return () => registerDirtyEditGuard?.(false);
  }, [editDirtyId, registerDirtyEditGuard]);

  // Leaving the PAGE with a dirty inline edit — sidebar, header, Back, reload
  // (R14, 2026-09-15; `hooks/useUnsavedChangesGuard.js`). `editDirtyId` ALONE,
  // for the same reason as the switch guard above: widening to a typed reject
  // reason is the owner's call, not done here. ONE guard for the page.
  const leaveGuard = useUnsavedChangesGuard({
    when: editDirtyId !== null,
    message: UNSAVED_ORDER_CHANGES_MESSAGE,
  });

  // ── DROP PER-DEPARTMENT EXPAND/EDIT STATE ON A SWITCH (2026-08-30) ──────
  // `DepartmentOrders.js` has carried this since 2026-08-17; `Approvals.js`
  // never had it. `fetchPendingApprovals`'s `activeDepartment?.department_id`
  // dependency already re-issues the queue request for the new department;
  // this clears the transient view-state at the MOMENT of the switch.
  //
  // 🕶️ **MEASURED, NOT ASSUMED — do not restate this as "editDirtyId stays
  // stuck forever, permanently pausing the poll".** That was the first
  // draft's claim and mutation testing disproved it
  // (`Approvals.departmentSwitchDirtyGuard.test.js`'s docblock has the full
  // account): `fetchPendingApprovals` sets `loading` non-silent on every
  // switch, `if (loading) return <LoadingSpinner />;` (below) unmounts the
  // WHOLE page including any open `OrderInlineEditor`, and that component's
  // own unmount cleanup already calls `onDirtyChange(false)` — so
  // `editDirtyId` (and with it the poll's `paused`) self-heals via that
  // EXISTING mechanism regardless of this effect. **What genuinely does NOT
  // self-heal, because it is plain page state rather than anything tied to a
  // child unmounting: `expandedOrders` and `orderDetails`.** Without this
  // effect, switching away and back re-expands a row nobody clicked (from a
  // surviving `expandedOrders` entry) showing a snapshot cached BEFORE the
  // round trip (`toggleExpand`'s own `!orderDetails[orderId]` guard skips the
  // refetch entirely) — a real, measured, non-vacuous bug this effect closes.
  // Owner ruling on WHY a dirty edit does not block a switch, and the newer
  // one on why it is announced first via a dialog rather than dropped
  // silently, are both at `DepartmentOrders.js`'s twin effect — read there,
  // not re-argued here; they still apply to `editDirtyId` even though its
  // OWN staleness turned out to be self-healing on this particular page.
  // 🔑 **This page reckons with AT MOST ONE dirty editor at a time** — the
  // table layout is a single-expand accordion specifically because
  // `GET /api/approvals/pending` selects the orders where the viewer IS the
  // current pending approver, so every row here is `can_edit: true` and
  // multi-expand would mean N simultaneous forms (see the sanctioned-exception
  // box in `pages/CLAUDE.md`). `editDirtyId` is already an id, not a Set, for
  // exactly that reason — nothing here widens that.
  // 🪤 `useRef` seeded with the current id: a no-op on first render and on the
  // initial undefined→id resolution, so it never disturbs a single-department
  // session and never double-fetches.
  const prevSwitchDeptRef = useRef(activeDepartment?.department_id);
  useEffect(() => {
    const deptId = activeDepartment?.department_id;
    if (prevSwitchDeptRef.current === deptId) return;
    prevSwitchDeptRef.current = deptId;
    setExpandedOrders(new Set());
    setOrderDetails({});
    setDetailsLoading({});
    setEditDirtyId(null);
    setPendingSaveAction(null);
  }, [activeDepartment?.department_id]);

  // Load cost codes for the active department
  //
  // 🔻 An `|| isAllDepartments` disjunct on the early return below, and a
  // per-ORDER cache (`hooks/useAllModeAccountingLists.js`) beside it, went
  // with the All Productions mode on 2026-08-31. They existed because in that
  // mode `activeDepartment.department_id` was a sentinel string that 403s
  // these endpoints, and because one page-level list cannot answer for the
  // several departments a row could belong to. With one department in view,
  // this effect is that answer again — which is what it was before the mode.
  useEffect(() => {
    // 🔑 AN EMPTIED OPTION LIST IS NOT A COSMETIC LOSS — here an APPROVER
    // reaching for the dropdown to correct a line that reads UNCODED can only
    // pick `-- None --`, which the cost-code PATCH writes as `null` over a real
    // code. That rule, why a non-ok response must throw so a refusal and a
    // rejection take the same path, and why the empty-department branch must
    // CLEAR the failure flags as well as the lists, are all in
    // `utils/orderReferenceLists.js`'s docblock (rule (a)).
    // 🔀 This was TWO effects with an identical dependency array until
    // 2026-10-03; one call now, because the shells were duplicated across three
    // surfaces. Same requests, same order.
    loadAccountingLists({
      departmentId: activeDepartment ? activeDepartment.department_id : null,
      entries: [
        { setList: setCostCodes, failureKey: 'costCodes' },
        { setList: setPaymentTypes, failureKey: 'paymentTypes' },
      ],
      setLoadFailures,
    });
  }, [activeDepartment]);

  // ── ALL-MODE ACCOUNTING LISTS — PER ORDER'S OWN DEPARTMENT (2026-08-31) ──

  // Reference data the inline edit form's dropdowns need.
  //
  // 🔑 ONCE PER PAGE, NOT ONCE PER ROW. These four lists are identical for every
  // order on the page, so fetching them inside the expanded row would issue four
  // requests every time somebody opened one. That is an N+1 on the client, where
  // `server/tests/api/n-plus-1-sweep.test.js` cannot see it. Byte-identical to
  // the twin on `DepartmentOrders.js`, including the empty dep array: they do
  // not vary with the active department (the server scopes each one to the
  // caller), and the two that DO — cost codes and payment types — already have
  // their own per-department effects above.
  //
  // ⚠️ It runs even for a viewer who will never see an editor. That is the
  // cheaper of the two mistakes: gating it on "is any row editable?" needs a
  // detail payload, which only exists after a row is expanded, which is exactly
  // when the lists are already needed.
  useEffect(() => {
    // 🪤 The `null` supplier sentinel, why `[]` cannot serve as one, why the
    // supplier entry catches for ITSELF, and the once-per-page rule, are all in
    // `utils/orderReferenceLists.js`'s docblock (rule (b)).
    // ⚠️ **THIS PAGE FETCHES FOUR LISTS, NOT FIVE, AND THE OMITTED
    // `setSupplierSuggestions` IS WHAT MAKES THAT TRUE.** It has never fetched
    // `/api/suppliers/name-suggestions`, so its picker offers records only — a
    // real divergence, not an omission here. The shared function issues no
    // fifth request in this mode, so the extraction could not quietly even the
    // counts up; and `suppliersFailed` is variadic, so the failure flag asks
    // only about the tier this page actually loaded.
    loadOrderReferenceLists({
      setProductions,
      setSuppliers,
      setSubDepartments,
      setGags,
      setLoadFailures,
    });
  }, []);

  // ── THE DECISION DOUBLE-SUBMIT GUARD (2026-08-13) ────────────────────────
  // `hooks/useSubmitGuard.js` — the OrderForm guard, shared. This page already
  // had per-order `actionLoading`, but that is a STATE flag set before the
  // await: the second click of a fast double-click lands before the re-render
  // that disables the button, and a double Approve advances TWO steps (the
  // first POST moves `current_step` on). The guard's ref drops the second call
  // in the same tick; `actionLoading` stays as the per-row spinner text, and
  // `decisionBusy` joins the `disabled` on BOTH copies of Approve/Reject (the
  // inline buttons AND their menu items).
  const { guard: guardDecision, busy: decisionBusy } = useSubmitGuard();

  /**
   * The empty-reason warning is a VALIDATION message for a reject attempt, and
   * it shares the page's one `error` slot with real failures. Owner 2026-10-09
   * (as a supervisor): cancel the reject, approve instead — "the 'Please provide
   * a reason for rejection' warning still shows even after clicking approve".
   * So abandoning the attempt (Cancel, Approve, reopening Reject, typing a
   * reason) clears THIS message only — never a load or action failure.
   */
  const clearRejectReasonError = () => setError((e) => (e === REJECT_REASON_REQUIRED ? null : e));

  const handleApprove = (orderId) => guardDecision(() => {
    clearRejectReasonError();
    // `expected_step_order`: the step this control was RENDERED against —
    // `/pending` flattens `oa.current_step` onto the row. OPTIONAL server-side
    // (absent → log-and-allow), so an unfindable row sends nothing rather than
    // guessing.
    const renderedStep = orders.find(o => o.id === orderId)?.current_step;
    setActionLoading(prev => ({ ...prev, [orderId]: 'approve' }));
    return apiPost(`${API_BASE}/api/approvals/orders/${orderId}/approve`, {
      notes: '',
      ...(Number.isInteger(renderedStep) ? { expected_step_order: renderedStep } : {}),
    })
      .then(res => {
        if (!res.ok) return res.json().then(d => { throw new Error(d.error || 'Failed to approve'); });
        return res.json();
      })
      .then(() => {
        setSuccessMessage('Order approved successfully!');
        setTimeout(() => setSuccessMessage(''), 3000);
        // Approved order leaves the pending list — drop it locally so the
        // page doesn't reload and lose scroll / expanded state.
        setOrders(prev => prev.filter(o => o.id !== orderId));
        window.dispatchEvent(new Event('sfx-approval-change'));
      })
      .catch(err => {
        setError(err.message);
      })
      .finally(() => {
        setActionLoading(prev => ({ ...prev, [orderId]: null }));
      });
  });

  const handleReject = (orderId) => guardDecision(() => {
    const notes = rejectNotes[orderId] || '';
    if (!notes.trim()) {
      setError(REJECT_REASON_REQUIRED);
      return undefined; // non-thenable → guard releases synchronously
    }
    setActionLoading(prev => ({ ...prev, [orderId]: 'reject' }));
    return apiPost(`${API_BASE}/api/approvals/orders/${orderId}/reject`, { notes })
      .then(res => {
        if (!res.ok) return res.json().then(d => { throw new Error(d.error || 'Failed to reject'); });
        return res.json();
      })
      .then(() => {
        setSuccessMessage('Order rejected.');
        setTimeout(() => setSuccessMessage(''), 3000);
        setShowRejectForm(prev => ({ ...prev, [orderId]: false }));
        setRejectNotes(prev => ({ ...prev, [orderId]: '' }));
        // Rejected order leaves the pending list — drop it locally.
        setOrders(prev => prev.filter(o => o.id !== orderId));
        window.dispatchEvent(new Event('sfx-approval-change'));
      })
      .catch(err => {
        setError(err.message);
      })
      .finally(() => {
        setActionLoading(prev => ({ ...prev, [orderId]: null }));
      });
  });

  /**
   * ── SAVE THE CURRENT STATE, *THEN* ACT (2026-08-09) ───────────────────
   *
   * Product owner, on the Approve button while the inline editor holds unsaved
   * edits: *"approve butotn should approve and save the current state"*.
   *
   * 🔑 **WHY, and it is not merely convenience:** what is on screen is what the
   * approver has just reviewed, so that is what they are approving. Approving
   * the STORED order while different figures sit in front of them would be an
   * approval of something they never read, with nothing saying so. It also sits
   * squarely on his standing ruling about why an approver may edit at all —
   * *"the log will say what changed and the later approvers are more senior so
   * they can overrule the ones before"* — so both the edit and the approval are
   * recorded and attributable.
   *
   * 🚨 **A FAILED SAVE MUST NEVER ACT, AND THAT IS STRUCTURAL HERE RATHER THAN
   * GUARDED.** `editorSaveRef.current()` resolves `true` only after a `res.ok`
   * PUT (see `OrderInlineEditor.js`), and `run()` is reached from ONE place:
   * inside that `true` branch. There is no `catch` anywhere that has to
   * remember not to approve, and no path from a refused PUT — or from a form
   * that failed its OWN validation, which issues no PUT at all — to an
   * approval. Same device `Save and close` uses for the collapse.
   *
   * 🟢 **A CLEAN FORM GOES STRAIGHT THROUGH, deliberately.** An empty diff
   * would still write an `order_audit_log` row and muddy the record that the
   * ruling above depends on, so there is no pointless PUT.
   *
   * ⚠️ **The reject REASON is not part of this.** `rejectNotes` is page state,
   * not a form field, so a save neither carries it nor can lose it — it is
   * still typed, still keyed by row id, and still travels with the reject
   * request exactly as it does today. A failed save leaves it untouched.
   *
   * 🔀 **AND SINCE 2026-08-10 THE SAVE IS EXPLICIT: A DIRTY ROW ASKS FIRST.**
   * Product owner: *"if there are unsaved changes and the user clicks approve
   * lets have a popup saying 'un saved changes!' then offer buttons like 'save
   * and approve' 'cancel' cancel doesnt undo the changes it just stops the
   * approval"*.
   * 🚨 **A REFINEMENT OF THE 2026-08-09 RULING QUOTED ABOVE, NOT A REVERSAL —
   * do not "restore" the silent save as a bug fix.** Everything in this docblock
   * still holds: the action applies to the SAVED order, a clean form goes
   * straight through with no PUT, and a failed save cannot act. What changed is
   * that the write is announced and declinable. Copy and the reasoning behind
   * every word are in `constants/saveThenActPrompt.js`.
   * 🔑 **THE PROMPT IS A GATE IN FRONT OF THE MACHINERY, NOT A REPLACEMENT.**
   * `saveThenRun` now only DECIDES; `performSaveThenRun` is the old body,
   * verbatim, and is the only thing that can reach `run()`.
   */

  /**
   * 🚨 **THE FAILED-SAVE-CANNOT-ACT GUARANTEE LIVES HERE AND IS STRUCTURAL.**
   * `run()` appears in exactly ONE place in this function — inside the `saved`
   * branch — and `saveThenRun`'s clean-form short-circuit is the only other
   * `run()` call on this page. No `catch` has to remember not to approve, and
   * the dialog added no third path: `Keep editing` returns without ever calling
   * this function.
   *
   * ⚠️ **`setActionLoading` moved IN HERE with the rest of the body, and that is
   * a behaviour improvement rather than an accident.** While the dialog is open
   * nothing is in flight, so the row's buttons must not read "Processing…" — the
   * spinner starts when the PUT does.
   */
  const performSaveThenRun = ({ orderId, kind, run }) => {
    const requestSave = editorSaveRef.current;
    // The editor unmounted between the click and the confirmation (a poll
    // refresh, a collapse). Nothing to save and nothing to act on.
    if (!requestSave) return;
    actionAfterSaveRef.current = orderId;
    setActionLoading(prev => ({ ...prev, [orderId]: kind }));
    requestSave().then((saved) => {
      actionAfterSaveRef.current = null;
      if (saved) { run(); return; }
      // The PUT was refused, or the form refused itself. The row stays open
      // showing the editor's own error banner or the form's field errors, the
      // edits are intact, and the order is NOT actioned.
      setActionLoading(prev => ({ ...prev, [orderId]: null }));
    });
  };

  /**
   * 🟢 **A CLEAN FORM STILL ACTIONS WITH NO PUT *AND NO PROMPT*.** Both halves
   * matter: on a page whose whole job is pressing Approve, a dialog in front of
   * every approval — including the overwhelming majority where nothing was
   * edited — would be the fastest way to teach users to dismiss it unread.
   * ⚠️ **The predicate is `editDirtyId !== orderId`, not "is anything dirty".**
   * Approving row 3 while row 7 holds unsaved edits prompts about nothing and
   * saves nothing; it never did, and must not start.
   */
  const saveThenRun = (orderId, kind, run) => {
    if (editDirtyId !== orderId || !editorSaveRef.current) { run(); return; }
    setPendingSaveAction({ orderId, kind, run });
  };

  // Reject needs its reason BEFORE any save: `handleReject` refuses an empty
  // one, and saving first would write an edit for an action that then never
  // happens.
  const handleConfirmReject = (orderId) => {
    if (!(rejectNotes[orderId] || '').trim()) {
      setError(REJECT_REASON_REQUIRED);
      return;
    }
    saveThenRun(orderId, 'reject', () => handleReject(orderId));
  };

  // 🔀 EXTRACTED 2026-10-03 INTO `hooks/useExpandedOrderCore.js`, which both
  // order-expansion hooks call too: `handlePoke`, `updateOrderListFromItem`,
  // `handleCostCodeChange`, `handlePaymentTypeChange` and
  // `handleItemNotifyUpdated` were byte-identical here and in both hooks.
  // 🔑 This page takes six of the core's nine handlers. It leaves
  // `handleOrderFilesChanged` (its own copy differs — it also closes the reject
  // form), `handleOrderDetailInvalidated` (no path reaches it here) and, since
  // the order cards and their inline Poke went (2026-10-18), `handlePoke` —
  // and the three it does not destructure cost it nothing: the core issues no request and
  // runs no effect.

  // `continueWhenClean` (above) guards every control that destroys an open
  // inline edit BY INTENT — expanding another row, and the links that
  // navigate to `/orders/:id`.

  /**
   * Toggle expand and lazy-load order details.
   *
   * 🟨 **AN ACCORDION — and that is the whole reason this page could take the
   * inline editor at all (2026-08-08).** (Until the order cards were retired,
   * 2026-10-18, the card layout was multi-expand.)
   *
   * It is an accordion, exactly like `DepartmentOrders.js`,
   * because there the expanded panel **is** an order form. Two open forms is not
   * a state this app supports: `components/OrderForm.js` hard-codes **58
   * id-referencing sites** (`id="supplier_name"` + the matching `htmlFor`,
   * `id="production_id-error"`, `id="supplier-suggestions-list"`,
   * `id="order-form"`) and **65 `data-testid`s**,
   * so a second instance on one page mis-associates every label — clicking the
   * third row's "Supplier" caption focuses the first row's input — and points
   * every `aria-describedby` at the wrong field.
   *
   * ⚠️ **THOSE TWO NUMBERS READ "~92" AND "~40" UNTIL 2026-08-08 AND BOTH WERE
   * WRONG — corrected here, in the test that cites them, and in the brief that
   * carried them.** Measured on the file: `id=` 31 (17 literal, 14 dynamic),
   * `htmlFor=` 23, `aria-describedby` 2, `aria-controls` 2 → **58**; testids
   * **65**. The 92 is what `grep -c 'id="'` returns, which matches *inside*
   * `data-testid="` and double-counts. 🔑 **The DESIGN does not depend on the
   * size — 58 colliding ids is exactly as fatal to two concurrent forms as 92
   * would be — so do not read the correction as undermining the accordion.**
   * 🪤 And note which attribute the old list omitted: **`aria-controls`**, which
   * is an id reference like any other. It is the shape of thing a "we scoped the
   * ids" pass leaves live, so count attributes, not one attribute. **And it is not an edge case
   * here: EVERY row on this page is `can_edit: true`**, because
   * `GET /api/approvals/pending` selects the orders where the viewer is the
   * current pending approver and `helpers/editPermissions.js:167-170` grants
   * exactly that person the edit. Scoping `OrderForm`'s ids with `useId` is the
   * real fix and is owed work; the accordion is what makes the feature correct
   * without it.
   *
   * ── Dirty guard — ONE ANSWER SINCE 2026-09-28 ──
   * Expanding row B while row A's form has unsaved changes would discard A's
   * edits (the accordion closes A), and collapsing A would too. Both open the
   * three-way prompt in `OrderInlineEditor.js`; B then opens on Save (ok) or
   * Discard. Expanding B was REFUSED with a pulse until the owner's
   * 2026-09-28 ruling (*"Trying to click on a tab or another row when we have
   * unsaved changes should cause the pop up to show"*).
   */
  const toggleExpand = useCallback((orderId, e) => {
    if (e) e.stopPropagation();
    if (editDirtyId !== null) {
      continueWhenClean(editDirtyId === orderId ? null : () => toggleExpandRef.current(orderId));
      return;
    }
    setExpandedOrders(prev => {
      const next = new Set(prev);
      if (next.has(orderId)) {
        next.delete(orderId);
      } else {
        // Fetch full order details if not already loaded
        if (!orderDetails[orderId] && !detailsLoading[orderId]) {
          setDetailsLoading(dl => ({ ...dl, [orderId]: true }));
          apiGet(`${API_BASE}/api/orders/${orderId}`)
            // W4 fix: a non-ok response (e.g. a stale-department 403) must NOT be
            // handed to `res.json()` and written into the cache — its body would
            // render as though it were the order (see `refreshOneOrder.js`'s
            // "fail to EMPTY, never fail to STALE" rule). Throwing here routes a
            // non-ok response into the same `.catch()` a network exception
            // already takes, which leaves the cache entry ABSENT — exactly what
            // this guard's own `!orderDetails[orderId]` re-fetches on the next
            // expand, and what the row's pre-existing "Failed to load order
            // details." fallback (OrderTableView.js) already
            // renders for.
            .then(res => {
              if (!res.ok) throw new Error(`Failed to load order ${orderId}: ${res.status}`);
              return res.json();
            })
            .then(data => {
              setOrderDetails(od => ({ ...od, [orderId]: data }));
              setDetailsLoading(dl => ({ ...dl, [orderId]: false }));
            })
            .catch(() => {
              setDetailsLoading(dl => ({ ...dl, [orderId]: false }));
            });
        }
        // Accordion — see the docblock.
        return new Set([orderId]);
      }
      return next;
    });
  }, [orderDetails, detailsLoading, editDirtyId, continueWhenClean]);
  toggleExpandRef.current = toggleExpand;

  // Dirty reporting from an open inline editor, keyed by row id so a closing
  // panel's unmount cleanup can only clear its own flag.
  const handleEditorDirtyChange = useCallback((orderId, isDirty) => {
    setEditDirtyId(prev => (isDirty ? orderId : prev === orderId ? null : prev));
  }, []);

  /**
   * Save landed: drop the guard, collapse the row, then refresh BOTH this
   * order's cached DETAIL and the list — in that order. All three steps are
   * required, and the reasoning is written out at the twin on
   * `DepartmentOrders.js`; the short version, because it is the rule that
   * generalises: **every mutation path on an order list must refresh the DETAIL
   * cache, not only the ones that look like status changes.**
   *
   * - The cache entry is dropped SYNCHRONOUSLY first, so a re-expand inside the
   *   refresh window finds an empty cache and fetches through the normal path
   *   rather than being served the pre-edit payload (which carries a stale
   *   `can_edit`, the very flag `showInlineEditor` gates on).
   * - `refreshOneOrder(..., { clearCanAction: false })` — an inline edit consumes
   *   no approval step, so asserting `can_action: false` would be a false
   *   assertion. `can_action` is not on the detail payload at all.
   * - `fetchPendingApprovals` LAST, because `refreshOneOrder` deliberately
   *   preserves `order_total` / `item_count` / `first_item_*` (the detail
   *   endpoint does not compute them) and an edit can change every one of them.
   *
   * ⚠️ This page's list is not merely refreshed, it can SHRINK: `PUT
   * /api/orders/:id` resubmits a rejected order, and an edit can change the
   * approval chain, so the order may no longer be waiting on this viewer. The
   * full refetch is what tells the truth about that; nothing here assumes the
   * row survives.
   */
  // An attachment was uploaded or removed in the expanded panel. The file list
  // lives on the DETAIL payload, so the component hands back the fresh
  // `GET /api/orders/:id` body and the page writes it into the detail cache.
  // 🔻 CORRECTED 2026-09-29: this said attaching a file "changes nothing the
  // collapsed row renders" — false since the paperclip (`file_count`, from
  // the LIST endpoint only), so the clip waited for the 30 s poll. The list is
  // now refetched silently (deferred while the poll is paused).
  const handleOrderFilesChanged = useCallback((orderId, freshDetail) => {
    if (!orderId || !freshDetail) return;
    setOrderDetails(prev => ({ ...prev, [orderId]: freshDetail }));
    requestListRefresh();
  }, [requestListRefresh]);

  // 🔻 `handleItemsReceived` WAS HERE AND IS GONE (2026-08-15). It fed the
  // expanded panel's "Items received" block, which is retired — the ticks live
  // in the LINE-ITEM ROWS now (`OrderItemReceivedControls` +
  // `utils/receivedItems.js` on DepartmentOrders/MyOrders). This page needs
  // none of that plumbing, and the old handler's own comment said why: every
  // order on To Approve is `submitted`, which is not a delivery-stage status,
  // so a received control can never render here. Do not "restore parity".

  const handleEditorSaved = useCallback((orderId) => {
    setEditDirtyId(null);
    setExpandedOrders(new Set());
    setOrderDetails(od => { const copy = { ...od }; delete copy[orderId]; return copy; });

    // 🚨 SAVE-THEN-ACT SHORT-CIRCUIT (2026-08-09). When this save was requested
    // BY Approve or Reject (`saveThenRun`), the action is about to remove this
    // order from the pending list, and it — not this handler — owns the
    // messaging. Running the refresh cascade as well is not merely redundant,
    // it RACES: `fetchPendingApprovals` would be in flight while the
    // approve/reject POST commits, so a response that lands after the action's
    // `setOrders(prev => prev.filter(...))` resurrects the row the user just
    // cleared. The action supersedes it.
    if (actionAfterSaveRef.current === orderId) return;

    setSuccessMessage('Order updated.');
    setTimeout(() => setSuccessMessage(''), 3000);
    refreshOneOrder(orderId, setOrders, setOrderDetails, { clearCanAction: false })
      .then(() => fetchPendingApprovals({ silent: true }));
  }, [fetchPendingApprovals]);

  /**
   * What the printed list says is filtering it.
   *
   * 🔑 **SEARCH IS THE ONLY NARROWING THIS PAGE HAS, so it is the only thing
   * here — and that is a real difference from Department Orders, not an
   * omission.** `Approvals.js` deliberately has NO status pills and NO
   * All/Actionable (every row is `submitted` and awaiting the viewer), so there
   * is no `describeOrderFilters` call to build from and no chip row to mirror.
   *
   * ⚠️ **Search is never a CHIP on any surface — and it must still appear HERE.**
   * The no-chip rule holds because search has its own always-visible input; a
   * sheet of paper has no input, so a search-narrowed queue would otherwise print
   * as though it were the whole queue.
   */
  const printFilterSummary = useMemo(
    () => (searchText && searchText.trim() ? `Search: "${searchText}"` : ''),
    [searchText]
  );

  const sortedOrders = useMemo(() => {
    let result = [...orders];
    if (searchText && searchText.trim()) {
      result = result.filter(o => orderMatchesSearch(o, searchText));
    }
    // Sort — THE order-list comparator (`utils/tableSort.js`).
    result.sort((a, b) => compareOrderRows(a, b, sortField, sortDir));
    return result;
  }, [orders, searchText, sortField, sortDir]);

  /**
   * ── `?order=<id>` DEEP LINK (2026-10-09) ──────────────────────────────
   * Owner, demo run-through: a bell "poke to approve" opened the old single
   * order page — *"Please load the order or hire in place"*. A viewer who
   * cannot see department orders but is a step's approver lands HERE
   * (`utils/notificationTarget.js`); the row is expanded in place.
   *
   * My Orders' resolve, minus what this page lacks (no tabs, no pagination):
   * not found → clear the search ONCE and retry; still not found → the order
   * is no longer waiting on this viewer (approved, rejected, or someone else's
   * step now), said in a toast rather than silently doing nothing. Found →
   * expand via the row's own `toggleExpand` (dirty-guarded like every other
   * expand) and scroll it under the pinned chrome.
   */
  const { error: toastError, info: toastInfo } = useToastOrNoop();
  const [pendingOrderId, setPendingOrderId] = useState(null);
  const deepLinkResetDone = useRef(false);
  useDeepLinkParam('order', (id) => {
    deepLinkResetDone.current = false;
    setPendingOrderId(id);
  });
  useEffect(() => {
    if (pendingOrderId == null || loading || error) return;
    const found = sortedOrders.some((o) => o.id === pendingOrderId);
    if (!found) {
      if (!deepLinkResetDone.current && searchText) {
        deepLinkResetDone.current = true;
        setSearchText('');
        toastInfo('Search was cleared to show this order.');
        return;
      }
      setPendingOrderId(null);
      toastError('That order is no longer waiting for your approval.');
      return;
    }
    const targetId = pendingOrderId;
    if (!expandedOrders.has(targetId)) toggleExpand(targetId);
    setPendingOrderId(null);
    setTimeout(() => {
      scrollUnderPinnedChrome(document.querySelector(`[data-testid="approval-order-${targetId}"]`));
    }, 150);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingOrderId, loading, error, sortedOrders]);

  /**
   * ── EXPORT THE APPROVAL QUEUE (2026-08-10) ────────────────────────────
   *
   * Product owner: *"to approve page actions menu should have export all excell
   * as we already have print all maybe even export pdf list. trying to stay
   * consistant"*.
   *
   * 🔓 **THIS SUPERSEDES A DELIBERATE OMISSION — record it as a RULING, not as
   * drift.** When `Actions ▾` landed on this page hours earlier, Export was
   * withheld with the reasoning *"this page has never had a page-level export;
   * adding it would be an unrequested capability, not a consistency fix"*. That
   * was correct at the time. **He has now asked for it**, so the omission is
   * over; do not re-derive the old argument and remove it again.
   *
   * 🔴 **NO NEW CAPABILITY IS GRANTED.** `GET /api/orders/export`
   * (`routes/orders.js:421`) has no admin check: it narrows in SQL through
   * `visibleOrdersClause` and post-filters every row through `canViewOrder`, so a
   * caller can only ever export rows they can already see. The per-row
   * `Export CSV` in this page's own row menu has hit the same endpoint since
   * 2026-08-10. Department Orders' copy dropped its `isAdmin` gate the same day
   * to match the server; **this page must not reintroduce one**, or the two
   * disagree about the same request.
   *
   * 🚨 **`ids` IS ALWAYS SENT HERE, AND THAT IS THE ONE REAL DIFFERENCE FROM
   * `DepartmentOrders.handleExport` — COPYING IT VERBATIM WOULD HAVE SHIPPED A
   * SILENTLY WRONG FILE.** That version omits `ids` when no filter is active,
   * which is right THERE because the unfiltered page *is* every order the viewer
   * can see. It is false HERE: `GET /api/approvals/pending` returns only the
   * orders where the viewer is the CURRENT pending approver, so an `ids`-less
   * request would answer "export all" with the viewer's whole department — a
   * plausible, correctly-formatted, wrong spreadsheet, which is exactly the
   * failure `Print all`'s scope subtitle exists to prevent and the reason nobody
   * would report it. **Do not "unify" the two handlers.**
   *
   * 🔑 `sortedOrders` is the search-narrowed list, and this page has NO
   * pagination at all — so there is no page slice to confuse "all" with.
   */
  const [exporting, setExporting] = useState(false);

  const handleExport = useCallback(async () => {
    setExporting(true);
    try {
      if (sortedOrders.length === 0) {
        toastInfo('No orders to export.');
        return;
      }
      const params = new URLSearchParams();
      params.set('format', 'csv');
      params.set('ids', sortedOrders.map(o => o.id).join(','));

      const response = await apiGet('/api/orders/export?' + params.toString());
      if (!response.ok) await apiThrow(response);

      const blob = await response.blob();
      const ext = CSV_EXT;
      // 🪤 `toLocalISO`, NOT the UTC date-slicing idiom — which is what
      // `DepartmentOrders.handleExport` does and what a copy-paste puts here. It
      // yields the UTC calendar date, so a user exporting at 00:30 BST gets a
      // file stamped YESTERDAY. Caught by
      // `server/tests/regressions/no-utc-date-drift.test.js`, whose ALLOWLIST
      // carries `DepartmentOrders.js` for that exact line ("orders-export
      // filename") — so the twin still drifts and this one does not. Reported,
      // not fixed here: changing a shipped filename is not this change's job.
      // ⚠️ The detector scans RAW TEXT, comments included, so do not quote the
      // banned idiom here even to warn about it — that is what made this
      // comment itself fail the check on its first draft.
      const filename = 'orders-to-approve-' + toLocalISO(new Date()) + ext;

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toastError(err.message || 'Export failed');
    } finally {
      setExporting(false);
    }
  }, [sortedOrders, toastError, toastInfo]);

  /**
   * A table header was clicked (the only sort control since the order cards
   * and their `Sort ▾` went, 2026-10-18).
   *
   * ⚠️ **Extracted 2026-08-08 from the inline `onSortFieldChange` this page used
   * to carry, and it is deliberately byte-identical to `DepartmentOrders.js`'s
   * `handleSort`.** The inline version had only the "new field" half — it set the
   * field and a default direction and had no toggle branch, because a `<select>`
   * never fires `onChange` for the value already selected, so re-picking was
   * unreachable. A header click IS reachable that way, so the toggle branch is
   * required. Keep the two pages identical rather than merely similar.
   *
   * **Third click keeps toggling; it does NOT return to the default sort.** That
   * is the settled house behaviour in both existing implementations —
   * `GagBreakdown.js`'s `changeSort` and `DepartmentOrders.js`'s `handleSort` —
   * and a "revert to default" third state is invisible in the caret, so a user
   * cannot tell which of three states a column is in.
   *
   * **Date and Total open DESCENDING, everything else ascending.** Newest and
   * biggest first is what a buyer wants and it is the direction these two fields
   * have already had through the dropdown since before headers existed; changing
   * it to satisfy a blanket "first click ascending" would alter a shipped
   * behaviour nobody complained about. Only `order_total` of the two has a
   * header.
   */
  // A row holding unsaved inline edits cannot be searched away. This page's
  // ONLY row-removing control is the search box — no pills, no All/Actionable,
  // no pagination — but one is enough: typing a search is not an instruction to
  // throw an edit away, and the editor unmounts (clearing `editDirtyId`) with
  // nothing left on screen to say what happened. Same choke-point treatment as
  // Department Orders; the reasoning lives in `utils/withDirtyRowPinned.js`.
  // ⚠️ The `sortedOrders.length === 0` no-results state below deliberately still
  // reads `sortedOrders`, so pinning a row cannot suppress the "no matching
  // approvals" message for a search that genuinely matched nothing.
  const visibleOrders = withDirtyRowPinned(sortedOrders, orders, editDirtyId);

  /**
   * @param {string} field
   * @param {'asc'|'desc'} [explicitDir]  supplied ONLY by a cycling header.
   *
   * 🔑 **KEPT BYTE-IDENTICAL TO `DepartmentOrders.js`'s** — CLAUDE.md requires
   * these two to be identical, not merely similar, and the cycling argument was
   * added to both in the same change for that reason.
   */
  // The rule itself is `utils/tableSort.js#applySortClick`, shared by all four tables.
  const handleSort = (field, explicitDir) => applySortClick(field, explicitDir, sortField, {
    setSortField, setSortDir, initialDir: orderInitialSortDir,
  });

  if (loading) return <LoadingSpinner />;

  // `Actions ▾` — one node, rendered in the header band on desktop and on
  // the phone's magnifier row (mobile batch, item 8); never both.
  const pageActionsMenu = hasHeaderActions ? (
    <Dropdown
      align="right"
      haspopup="menu"
      panelClassName="w-64 p-1"
      // 📱 `max-md:px-2` only on the phone row (mobile batch); desktop markup byte-identical.
      triggerClassName={isMobile ? `${TOOLBAR_DISCLOSURE_TRIGGER} max-md:px-2` : TOOLBAR_DISCLOSURE_TRIGGER}
      title="Page actions"
      ariaLabel="Page actions"
      triggerTestId="approval-actions-menu"
      label="Actions"
    >
      {({ close }) => (
        <div role="menu" aria-label="Page actions">
          {/* Same copy, same disabled state and same tooltips as
              Department Orders and the Breakdown. One behaviour, three
              surfaces — do not reword it here.

              🔑 **When a container's gate is relaxed, every item that was
              relying on it needs its own** (learned 2026-08-10 on this menu's
              column-width item; its table-only gate went with the order
              cards, 2026-10-18). */}
          {/* 🔓 **EXPORT ARRIVED HERE ON 2026-08-10 AND SUPERSEDES A
              DELIBERATE OMISSION MADE HOURS EARLIER — an OWNER RULING, not
              drift.** Product owner: *"to approve page actions menu should
              have export all excell as we already have print all maybe
              even export pdf list. trying to stay consistant"*. The block
              that stood here said *"STILL NO EXPORT HERE … a brand-new
              capability HERE is unrequested"*; it was right until he
              requested it. **Do not restore that argument.**

              🔴 **CSV IS HERE TOO, DECIDED RATHER THAN COPIED.**
              Consistency is his stated driver, the gate is identical, and
              CSV is a second FILE FORMAT of a capability being added
              anyway — not a new capability class. Leaving it out would
              recreate the "why there and not here" that produced the
              request.

              🚨 **`handleExport` here ALWAYS sends `ids`, unlike Department
              Orders' — see its docblock.** Omitting them would answer
              "export all" with the viewer's whole department instead of
              their approval queue.

              🔑 No filtered-results hint above these rows, unlike
              Department Orders: this page's only narrowing is SEARCH,
              which has its own always-visible input two inches away, and
              the export is always explicitly scoped to the rows on
              screen. */}
          <button
            type="button"
            role="menuitem"
            onClick={() => { close(); handleExport(); }}
            disabled={exporting}
            title={`Download the orders awaiting your approval as ${CSV_LABEL}`}
            className={ORDER_ACTIONS_MENU_ITEM}
            data-testid="approval-actions-export-csv"
          >
            {exporting ? 'Exporting…' : CSV_MENU_LABEL}
          </button>
          {/* 🔀 PRINT ALL (2026-08-10). Product owner: *"print all is a
              printable list, likey landscape to fit all columns on."*
              Same component, same document, same landscape builder as
              Department Orders — this page is a list of orders and the
              approval queue is exactly the thing people carry on paper.

              🔀 **`downloadTestId` ADDS `Download list (PDF)`** — *"maybe
              even export pdf list"*. It lands on BOTH pages in the same
              change: putting it on one only would have made them LESS
              consistent while acting on a consistency request.

              🚨 `sortedOrders` is the whole filtered list — this page has
              no pagination at all, so there is no slice to confuse it
              with, but the document still states its own scope. */}
          <OrderListPrintMenuItem
            orders={sortedOrders}
            headerTitle="Orders To Approve"
            departmentName={activeDepartment?.department_name || ''}
            filterSummary={printFilterSummary}
            // 🚨 FIXED 2026-09-28: identical one-token bug to Dept
            // Orders' print menu item — this read `orders.length` (the
            // UNFILTERED list) while `sortedOrders` above already
            // narrows through the search box (`:1084-1088`), so a
            // search term made `listPdf.js#reconciliationLine` print a
            // false "caller reported" note on an already-correct
            // document. `totalCount` must count the rows actually
            // handed to the printer.
            totalCount={sortedOrders.length}
            filename="orders-to-approve.pdf"
            onDone={close}
            testId="approval-actions-print-all"
            downloadTestId="approval-actions-download-list"
          />
          {/* 🔀 FIT COLUMNS TO SCREEN — REPLACES `Reset column widths`
              (owner ruling 2026-09-28: *"reset column widths should
              use the same code and changes as Fit columns to screen
              on the Breakdown page. same for other tables."*). See
              `DepartmentOrders.js`'s identical item for the full
              reasoning. Stays at the BOTTOM of the menu — the four
              take-it-away outputs first, the table-gated layout
              utility last. Contract change; see
              `orderColumnResize.wiring.test.js` §3. */}
          <button
            type="button"
            role="menuitem"
            onClick={() => { close(); fitColumnsToScreen(); }}
            disabled={!canFitColumns || columnsAlreadyFit}
            title={!canFitColumns
              ? 'Nothing measured to fit to yet'
              : columnsAlreadyFit
                ? 'The columns already fit the screen'
                : 'Fit every column to the available screen width'}
            className={ORDER_ACTIONS_MENU_ITEM}
            data-testid="approval-actions-fit-columns"
          >
            Fit columns to screen
          </button>
        </div>
      )}
    </Dropdown>
  ) : undefined;

  return (
    <div className="p-2.5 pt-0 max-w-full">
      {leaveGuard.dialog}
      {/* ── HEADER BAND — the shared `ui/PageHeaderBand`, same shell as the other
          three pages. Read its docblock for the two-band model.

          🚨 THIS PAGE PASSES NO `actions`, AND THAT IS THE CORRECT OUTCOME, not
          an omission to finish. It has no Refresh, no Export and no primary
          create — so its header band is context + status only, and it therefore
          has NO FILLED CONTROL anywhere in its chrome. The filled weight is
          reserved for a primary create (see the three-weights block in
          `components/ui/Toolbar.js`); promoting something else into the slot to
          make the page look "complete" is exactly what that rule forbids.
          `PageHeaderBand` renders no empty flex child for an omitted slot, so
          the hairline between status and actions does not appear either.

          🔻 A MIDDOT, NOT PARENTHESES — the same change made on Department
          Orders, applied here for cross-page consistency: this count IS the page
          summary, and brackets whisper. */}
      {/* 📱 Below `md` the band's context + `Actions ▾` move onto the sticky
          magnifier row below (mobile batch, item 8) — unless the list is empty,
          when there is no toolbar and the band is the whole header. */}
      {(!isMobile || orders.length === 0) && (
      <PageHeaderBand
        testId="approval-header-band"
        context={<>Orders waiting for your approval &middot; {orders.length}</>}
        /* 🔻 NO `status` SLOT SINCE 2026-08-08 — the VAT badge was the only thing
           that ever occupied it here, and the VAT display was removed from the
           UI by product decision (the reason, which is what stops someone
           restoring it, is recorded in `hooks/useVatMode.js`: one boolean cannot
           express VAT rates that vary per line item). The band now carries
           `context` alone, which is a shape `PageHeaderBand` already supports and
           its own tests pin. ⚠️ Do not fill this slot with a placeholder to
           "balance" the row — an omitted slot renders NO node, deliberately. */
        /**
         * 🔀 **THE `actions` SLOT IS NO LONGER ALWAYS EMPTY (2026-08-10), AND
         * THAT IS A DELIBERATE ADDITION.** Product owner: *"…also maybe an
         * action menu on the to approve page in a similar style?"* This page has
         * never had a header-band action; it gets one now, holding exactly one
         * item, matching Department Orders' and the Breakdown's `Actions ▾`.
         *
         * 🚨 **The `: undefined` on `pageActionsMenu` is load-bearing.**
         * `PageHeaderBand` decides with `Boolean(actions)`, so a fragment that
         * renders nothing is still truthy and would draw an empty right-hand
         * flex child — the rule `hasHeaderActions` above carries.
         *
         * 🔻 **BOTH OF THIS BLOCK'S EXCLUSIONS HAVE SINCE BEEN LIFTED BY THE
         * OWNER, AND THE ORIGINAL TEXT IS KEPT SO NOBODY RE-DERIVES THEM.** It
         * read: *"🔴 NO Export here. Department Orders' export is admin-gated and
         * this page has never had one; adding it would be an unrequested
         * capability, not a consistency fix. And no 'Print all' — the owner has
         * not decided what it means."* Both were sound when written and both are
         * now superseded: `Print all` by *"print all is a printable list, likey
         * landscape to fit all columns on"*, and Export by *"to approve page
         * actions menu should have export all excell as we already have print
         * all maybe even export pdf list. trying to stay consistant"*. The
         * admin-gate half was also overtaken independently — Department Orders'
         * export is no longer `isAdmin`-gated; it matches the server, which
         * allows anyone to export what they can already see.
         *
         * 🔑 **The MENU now holds four items and both order pages carry the same
         * four, in the same order** — `Export CSV` (2026-10-28: the separate `Export Excel` item was folded into it) ·
         * `Download list (PDF)` · `Print all` · `Fit columns to screen`. Consistency between the two was
         * the request, so treat the pair as the contract rather than the
         * individual items; `orderColumnResize.wiring.test.js` §3 asserts ONE
         * shared array against both pages for exactly that reason.
         */
        actions={pageActionsMenu}
      />
      )}

      {/* Toolbar — the same sticky shell as Department Orders and My Orders, so
          the three order pages share one bar. What this page does NOT get, and
          why it is a justified per-page variation rather than an omission:

          - NO status pills. Every order on this page is `submitted` and waiting
            on the viewer, so a multi-select over a single status would filter
            nothing. There were none here before either; this is not a removal.
          - NO All / Actionable. Every row qualifies as actionable by
            definition, so the toggle would be a no-op — which is also why
            "actionable" has no meaning to unify across the three pages.
          - NO active-filter chip row, because with no filters there is never a
            chip. The chip row exists to expose state hidden behind a
            disclosure; this page hides none.

          Search is its only list control; the table's column headers sort (the
          Cards ⇄ Table toggle and the cards-only `Sort ▾` went with the order
          cards, 2026-10-18).

          ⚠️ THE GATE IS ON `orders`, THE RAW LIST — NOT ON THE FILTERED SET,
          and it is KEPT deliberately. It reads like the classic "the search box
          vanishes when a filter empties the page" bug and it is not that bug:
          `sortedOrders` is what search narrows, `orders` is what the endpoint
          returned, so typing a search that matches nothing leaves the bar
          exactly where it was. The only state it hides the bar in is a
          genuinely empty To Approve list, where "All caught up!" is the whole
          message and a search box over nothing would be noise.
          The REAL defect of that shape on this page was the missing no-results
          state further down, which is fixed in the same commit. */}
      {orders.length > 0 && (
        <Toolbar className={PHONE_HEADER_TOOLBAR}
          ref={toolbarRef}
        >
          {isMobile ? (
            /* 📱 PHONE (mobile batch, item 8): the search box hid a whole row
               of the sticky chrome on its own. It now sits behind the shared
               magnifier, on ONE row with the header band's context and
               `Actions ▾` — the Dept Orders row, minus tabs (this page has
               none, and must not gain any). A phone is always the table, so no
               `Sort ▾` is lost: the headers sort.
               2026-10-03: the shared phone header with NO row 1 (no tabs) —
               row 2 is 🔍 · the context note · Actions ▾. */
            <MobileTabSearchRow
              idPrefix="approval"
              searchTestIdPrefix="approval"
              searchText={searchText}
              onSearchChange={setSearchText}
              searchPlaceholder="Search pending approvals…"
              searchLabel="Search pending approvals"
              note={(
                <p
                  className="flex-1 min-w-0 truncate text-sm text-gray-500 dark:text-gray-400"
                  data-testid="approval-mobile-context"
                >
                  Waiting for your approval &middot; {orders.length}
                </p>
              )}
              actions={pageActionsMenu}
            />
          ) : (
          <OrderSearchAndSort
            searchText={searchText}
            onSearchChange={setSearchText}
            searchPlaceholder="Search pending approvals…"
            testIdPrefix="approval"
          />
          )}
        </Toolbar>
      )}

      {/* Success Message */}
      {successMessage && (
        <div className="mb-4 p-3 bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-800 rounded-lg text-green-800 dark:text-green-300 text-sm">
          {successMessage}
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="mb-4">
          <ErrorMessage error={error} />
        </div>
      )}

      {/* ── NO PENDING APPROVALS — AND THE COPY NAMES THE SCOPE (2026-08-12) ──
          🚨 THE OLD SENTENCE BECAME A FALSE STATEMENT THE DAY THIS LIST WAS
          DEPARTMENT-SCOPED, WHICH IS WHY IT IS NOT MERELY A WORDING TWEAK. It
          read *"No orders are currently waiting for your approval."* — an
          unqualified claim about the viewer, which was exactly right while the
          endpoint spanned every department and is wrong now: orders may well be
          waiting for them one department away, and this page will never say so.
          A user told "all caught up" has no reason to go and look.

          🔑 THE DEPARTMENT NAME IS THE WHOLE FIX. It is the only thing on the
          page that tells someone WHERE the empty answer applies, and it is the
          affordance that points at the switcher — the owner accepted the
          invisibility of a cross-department approval knowingly (*"we are not
          expecting someone to be running multiple department just yet"*), so
          saying which department this is, is the honest minimum.

          🔴 THREE CASES, DELIBERATELY DISTINCT — do not collapse them:
          - SITE ADMIN keeps the original unqualified sentence, because for an
            admin the list genuinely does span departments and naming one would
            be the false claim in the other direction.
          - NO ACTIVE DEPARTMENT gets its own explanation rather than a silent
            empty list. Server-side this means the caller holds NO assignment
            anywhere (`middleware/departmentScope.js` defaults the active
            department to the caller's first assignment, and even a personal My
            Space is one), so it is the offboarded-approver case — the filter
            fails CLOSED there and returns nothing, by design.
          - Everyone else gets the department named.
          ⚠️ `activeDepartment` is also null for a frame BEFORE the context
          resolves, which is a different thing from holding no assignment. The
          page renders `<LoadingSpinner />` while `loading` is true, so that
          frame does not reach here — but if this block is ever moved above that
          guard, the no-department copy would flash at every user on every load.
          🚨 AND `activeDepartment` IS ALSO NULL WHEN THE ASSIGNMENT REQUEST
          FAILED — 2026-08-22. That is a third state, not a variant of the
          second: the caller may hold a dozen seats and we simply did not get
          to ask. Sending them to "Ask an administrator to add you to one" is
          the false claim this whole change exists to remove, so
          `assignmentsError` selects its own copy BEFORE the no-department
          branch is consulted. Same shape as the `!error` term this block has
          carried since 2026-08-08, one layer up: `error` covers the approvals
          fetch, `assignmentsError` covers the membership fetch, and only when
          BOTH landed is an empty list a fact about the world. */}
      {orders.length === 0 && !error && (
        <EmptyState
          icon={
            /* The GREEN TICK is the "all caught up" reassurance and must not
               survive into the failure state — a green all-clear over the words
               "couldn't load" is the same conflation this change removes, drawn
               instead of written. Amber warning triangle when we could not ask. */
            assignmentsError ? (
              <svg className="mx-auto h-14 w-14 text-amber-500 dark:text-amber-400 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
              </svg>
            ) : (
              <svg className="mx-auto h-14 w-14 text-green-400 dark:text-green-500 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            )
          }
          title={
            assignmentsError
              ? 'Couldn’t load your departments'
              : !isAdmin && !activeDepartment ? 'No department selected' : 'All caught up!'
          }
          description={
            assignmentsError
              ? 'We could not check which departments you belong to, so this list may be incomplete. Reload the page to try again.'
              : isAdmin
                ? 'No orders are currently waiting for your approval.'
                : !activeDepartment
                  ? 'You are not currently assigned to a department, so there is nothing to approve here. Ask an administrator to add you to one.'
                  : `No orders in ${activeDepartment.department_name || 'this department'} are waiting for your approval. Approvals in another department appear when you switch to it.`
          }
          testId="approval-empty-state"
        />
      )}

      {/* Search matched nothing. There ARE approvals waiting — the search just
          hid them — and until 2026-08-08 this case rendered no message at all:
          on cards an entirely blank page, on the table a header row with
          nothing under it, in both cases indistinguishable from "all caught
          up" while the box still held the text that caused it. The other two
          order pages both have this state; this page was the odd one out. */}
      {orders.length > 0 && sortedOrders.length === 0 && !error && (
        <EmptyState
          size="sm"
          title="No matching approvals"
          description={`No pending approvals match “${searchText.trim()}”. Try a different search term.`}
          testId="approval-no-results"
        />
      )}

      {/* Pending Orders List — the table (desktop) or the phone table. */}
      <OrderTableShell
        testIdPrefix="approval"
        columns={activeTableColumns}
        sortField={sortField}
        sortDir={sortDir}
        onHeaderSort={handleSort}
        stickyTop={headerTop}
        columnWidths={activeColumnWidths}
        onColumnResize={activeSetColumnWidth}
        onColumnResetWidth={activeResetColumnWidth}
        onRegionWidthChange={setTableRegionWidth}
      >
        {visibleOrders.map(order => {
          const isExpanded = expandedOrders.has(order.id);
          const detail = orderDetails[order.id];
          const isLoadingDetail = detailsLoading[order.id];

          // ════════════════════════════════════════════════════════════════
          // THE ROW ACTIONS — ONE DEFINITION, RENDERED BY BOTH PANEL BRANCHES
          // ════════════════════════════════════════════════════════════════
          // 🔴 **HOISTED OUT OF THE READ-ONLY PANEL ON 2026-08-09 TO FIX A
          // FUNCTIONAL REGRESSION THE PRODUCT OWNER HIT:** *"just expanded an
          // order on my to approve page and it has no approve button?"*
          //
          // 🕶️ **THE PREMISE THAT SHIPPED IT, because it is the reasoning that
          // stops someone rebuilding the same shape.** Expand-to-edit was
          // designed as read-only-panel **OR** edit-form — one `? :` over the
          // whole panel — which is right on Department Orders, where most rows
          // are not editable and the read-only branch is the common case. On
          // THIS page it is fatal: **every row here is `can_edit: true`**
          // (`GET /api/approvals/pending` selects the orders where the viewer
          // IS the current pending approver — see `toggleExpand`'s docblock),
          // so in the table layout the editor branch ALWAYS wins and the
          // "otherwise" branch is dead code. It was carrying Approve, Reject
          // and View Full Details: the page's only reason to exist. The card
          // layout never took the editor, which is why nothing caught it.
          //
          // 🔑 **THE RULE THAT REPLACES IT: editing and approving are not
          // alternatives.** An approver routinely wants to correct something
          // *and then* approve it — which is the owner's own justification for
          // letting approvers edit at all (*"I dont mind approvers being able
          // to change the order as the log will say what changed"*). So these
          // controls are rendered by BOTH branches from this ONE definition,
          // and any future third branch must render them too.
          //
          // 🚨 **VISIBILITY STILL DEPENDS ON THE APPROVAL CHAIN, NEVER ON
          // `can_edit`** — the two questions are kept apart. Every row on this
          // list is one the viewer is the pending approver for, which is what
          // puts it here at all; nothing below consults the edit flag.
          // `OrderTableView.js` still receives a finished node and decides
          // nothing, per the sanctioned-exception box in CLAUDE.md.
          const approvalActionButtons = (
            <div className="w-full md:w-auto grid grid-cols-2 gap-2 md:flex md:flex-wrap md:items-center">
              {/* 🔻 **`View Full Details` LEFT THIS ROW ON 2026-08-10 and was a
                  menu item until 2026-10-04, when the owner had it deleted
                  outright ("seems like the expanded order gives us the
                  info") — see `requiredActions.manifest.test.js`.** Product
                  owner: *"replace 'View Details / Edit
                  / Approve / Reject / Download / Print / Status Override …'
                  actions with the single actions menu"*.
                  ⚠️ **This partially supersedes his 2026-08-08 ruling** — *"on to
                  approve page put 'View Full Details / Reject / Approve' next to
                  the PO box. not on a row above"* — and the part it supersedes is
                  only WHICH of those three sit beside the PO box. Approve and
                  Reject still do, which is the half that ruling was really
                  about (pointer travel from the chevron); View Full Details is
                  the escape hatch, not the action, and it is the first thing he
                  named in the replace list. **Recorded rather than quietly done:
                  if he wants it back on the row, it is one item moved.** */}

              {/* 🚨 **EVERY BUTTON IN THIS GROUP CARRIES `type="button"`, AND IT
                  IS LOAD-BEARING RATHER THAN TIDINESS — ADDED 2026-08-11 WHEN
                  THE GROUP MOVED INTO `OrderForm`'s `Order Details` HEADER.**
                  `OrderForm`'s root element is `<form id="order-form">`, and a
                  `<button>` with no `type` inside a form defaults to
                  **`type="submit"`**. So the move — which is supposed to be
                  purely positional — silently turned Approve, Reject, Cancel and
                  Confirm Reject into form-submit buttons: each one ran its own
                  handler AND submitted the edit form, issuing an extra
                  `PUT /api/orders/:id` nobody asked for.
                  🔑 **This ADDS no behaviour and REMOVES none — it restores the
                  behaviour these buttons had when they sat outside a form.**
                  Measured, not reasoned: without it
                  `Approvals.approveWithEdits.test.js` §6 sees **3** `apiPut`
                  calls where it expects 1, and the two-step reject never reveals
                  its textarea.
                  ⚠️ **Any control added to this group from now on needs it too**
                  — the failure is silent in a browser (the row simply saves
                  twice) and the group no longer lives where its author can see
                  the `<form>` it is inside. */}
              {/* 📱 Pinned to the bottom on a phone, away from Actions ▾, and
                  stepped aside while the form has unsaved changes (owner
                  2026-10-04) — `ui/PhoneDecisionBar`. Desktop unchanged. The
                  reason box rides IN the bar on a phone (beside its Confirm);
                  the header copy below is desktop-only. */}
              <PhoneDecisionBar
                testId={`appr-decision-bar-${order.id}`}
                above={showRejectForm[order.id] ? (
                  <textarea
                    value={rejectNotes[order.id] || ''}
                    onChange={(e) => { clearRejectReasonError(); setRejectNotes(prev => ({ ...prev, [order.id]: e.target.value })); }}
                    placeholder="Reason for rejection (required)..."
                    className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-red-500 focus:border-transparent"
                    rows={2}
                    data-testid={`reject-notes-phone-${order.id}`}
                  />
                ) : null}
              >
              {showRejectForm[order.id] ? (
                <>
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); clearRejectReasonError(); setShowRejectForm(prev => ({ ...prev, [order.id]: false })); }}
                    className="inline-flex items-center justify-center md:justify-start min-h-[44px] md:min-h-0 px-3 py-1.5 text-sm font-medium text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    // Saves any unsaved edits FIRST and rejects only if that
                    // save landed — see `saveThenRun`. The typed reason is page
                    // state, so it is neither carried by the save nor lost by a
                    // failed one.
                    onClick={(e) => { e.stopPropagation(); handleConfirmReject(order.id); }}
                    disabled={actionLoading[order.id] === 'reject' || decisionBusy}
                    className="inline-flex items-center justify-center md:justify-start min-h-[44px] md:min-h-0 px-4 py-1.5 text-sm font-medium bg-red-50 text-red-700 hover:bg-red-100 dark:bg-red-900/30 dark:text-red-300 dark:hover:bg-red-900/50 rounded-lg disabled:opacity-50 transition-colors"
                    data-testid={`confirm-reject-${order.id}`}
                  >
                    {actionLoading[order.id] === 'reject' ? 'Rejecting...' : 'Confirm Reject'}
                  </button>
                </>
              ) : (
                <>
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); clearRejectReasonError(); setShowRejectForm(prev => ({ ...prev, [order.id]: true })); }}
                    disabled={!!actionLoading[order.id] || decisionBusy}
                    className="inline-flex items-center justify-center md:justify-start gap-1.5 min-h-[44px] md:min-h-0 px-3 py-1.5 text-sm font-medium bg-red-50 text-red-700 hover:bg-red-100 dark:bg-red-900/30 dark:text-red-300 dark:hover:bg-red-900/50 rounded-lg disabled:opacity-50 transition-colors"
                    data-testid={`reject-btn-${order.id}`}
                    title="Send back to the submitter for correction — they can edit and resubmit"
                  >
                    Reject
                  </button>
                  <button
                    type="button"
                    // Saves the current state FIRST and approves only if that
                    // save landed. A clean form approves with no PUT at all.
                    onClick={(e) => { e.stopPropagation(); saveThenRun(order.id, 'approve', () => handleApprove(order.id)); }}
                    disabled={!!actionLoading[order.id] || decisionBusy}
                    className="inline-flex items-center justify-center md:justify-start gap-1.5 min-h-[44px] md:min-h-0 px-4 py-1.5 text-sm font-medium bg-green-50 text-green-700 hover:bg-green-100 dark:bg-green-900/30 dark:text-green-300 dark:hover:bg-green-900/50 rounded-lg disabled:opacity-50 transition-colors"
                    data-testid={`approve-btn-${order.id}`}
                    title="Approve this order and advance it to the next approval step"
                  >
                    {actionLoading[order.id] === 'approve'
                      ? 'Processing...'
                      : approveButtonLabel(order.collapsible_step_count, order.current_step_hidden)}
                  </button>
                </>
              )}
              </PhoneDecisionBar>

              {/* ════════════════════════════════════════════════════════════
                  THE ACTIONS MENU — the SAME COMPONENT Department Orders uses,
                  with a DIFFERENT ITEM LIST, and that split is the design.
                  ════════════════════════════════════════════════════════════
                  This page has no status lifecycle, no admin notes, no restore
                  and no status override, so it carries none of them. What the
                  two pages share is the CHROME and the placement rule; the
                  items and their gates are written once per page, beside the
                  controls they replace. **Do not "unify" the two arrays.**

                  🚨 **APPROVE AND REJECT ARE HERE *AND* VISIBLE ABOVE — two
                  triggers, ONE handler each, DISTINCT testids.** The visible
                  pair keeps `approve-btn-${id}` / `reject-btn-${id}`, which
                  `requiredActions.manifest.test.js` pins; the menu items take
                  `approval-menu-*`. Two components stamping one testid is a
                  documented defect class here (`container.querySelector`
                  returns the first match silently), so a
                  `querySelectorAll(...).length === 1` guard covers the pair.

                  🔑 **Approve keeps `saveThenRun` and Reject keeps the two-step
                  reveal.** The menu's Reject sets the same `showRejectForm`
                  flag the visible button sets, so it reveals the same textarea
                  and is consumed by the same `Confirm Reject` — a second reject
                  path would be a second thing to keep in step. Its `close()`
                  runs after, so the panel dismisses onto the revealed form.

                  🆕 **Download / Print / Share are NEW ON THIS PAGE** — it never
                  had an `OrderPdfButton`. Taken deliberately on 2026-08-10
                  because the owner asked for "print options in the action menu"
                  and this is an order surface with the same detail payload;
                  recorded as an ADDITION, not a relocation, so it is not later
                  read as drift. */}
              <OrderActionsMenu
                triggerTestId={`approval-actions-menu-${order.id}`}
                panelTestId={`approval-actions-panel-${order.id}`}
                ariaLabel={`Actions for order ${getOrderNumber(order)}`}
                idPrefix={`approval-actions-${order.id}`}
                // 🚨 CATEGORIES ARE PRESENTATION — no gate moved. Same comment
                // as `DepartmentOrders.js`.
                // 🔴 **TWO categories, not three, because this page has no
                // status lifecycle at all** — the same per-page split the item
                // arrays already have. Do not "complete the set" with an empty
                // `Status`: it would be dropped anyway, so declaring it would
                // only be a lie in the source.
                // 🔀 2026-09-13 (Pete, "sounds great please proceed") — the
                // surviving category is `Next step`, not `Approval`, and it is
                // now FIRST: the same merge/reorder that landed on
                // `DepartmentOrders.js`. This page had no `Status` group to
                // merge in (every row here is `submitted`), so the change is
                // the rename + reorder alone; see that page's categories block
                // for the ruling in full.
                categories={[
                  { key: 'next', label: 'Next step' },
                  { key: 'order', label: 'Order' },
                ]}
                items={[
                  // 🔻 `View Full Details` REMOVED (owner, 2026-10-04: "seems
                  // like the expanded order gives us the info"), removed from
                  // all three order list Actions menus in the same change.
                  // The /orders/:id PAGE STAYS — bell notifications, Hires
                  // pages and NewOrder's "View Order" still link to it; only
                  // this menu item is gone. Pre-deletion SHA 204a6b95.
                  // Approve — the SECOND trigger. Same `saveThenRun`, so a dirty
                  // form is saved first and a FAILED save can never approve.
                  {
                    key: 'approve', category: 'next', // 🔀 2026-09-13 — was 'approval'
                    label: approveButtonLabel(order.collapsible_step_count, order.current_step_hidden),
                    tone: 'approve',
                    disabled: !!actionLoading[order.id] || decisionBusy,
                    testId: `approval-menu-approve-${order.id}`,
                    title: 'Approve this order and advance it to the next approval step',
                    onClick: () => saveThenRun(order.id, 'approve', () => handleApprove(order.id)),
                  },
                  // Reject — reveals the SAME reason textarea the visible button
                  // reveals. It issues no request itself; `Confirm Reject` does.
                  {
                    key: 'reject', category: 'next', // 🔀 2026-09-13 — was 'approval'
                    label: 'Reject',
                    tone: 'reject',
                    disabled: !!actionLoading[order.id] || decisionBusy,
                    testId: `approval-menu-reject-${order.id}`,
                    title: 'Send back to the submitter for correction — they can edit and resubmit',
                    onClick: () => { clearRejectReasonError(); setShowRejectForm(prev => ({ ...prev, [order.id]: true })); },
                  },
                  // Export CSV — this order and its line items. Ungated; the
                  // measurement behind that is at `utils/exportSingleOrder.js`.
                  {
                    key: 'export-csv', category: 'order',
                    label: CSV_MENU_LABEL,
                    testId: `approval-menu-export-csv-${order.id}`,
                    title: `Download this order and its line items as ${CSV_LABEL}`,
                    onClick: async () => {
                      try {
                        await exportSingleOrderCsv(detail || order);
                      } catch (err) {
                        setError(err.message);
                      }
                    },
                  },
                  {
                    key: 'pdf', category: 'order',
                    render: (close) => (
                      <OrderPdfMenuItems
                        order={detail}
                        onDone={close}
                        testIdPrefix={`approval-${order.id}`}
                      />
                    ),
                  },
                ]}
              />
            </div>
          );

          // The reject reason. 🔑 IT TRAVELS WITH THE BUTTONS AND MUST STAY
          // DIRECTLY BENEATH THEM in whichever branch renders them: the flow is
          // click Reject → this appears → type → Confirm Reject, so the control
          // that REVEALS it and the control that CONSUMES it must not be
          // separated by a panel's height. Below the buttons rather than above,
          // so the panel does not jump when it opens.
          const rejectNotesField = showRejectForm[order.id] ? (
            <div className="mb-3 max-md:hidden">
              <textarea
                value={rejectNotes[order.id] || ''}
                onChange={(e) => { clearRejectReasonError(); setRejectNotes(prev => ({ ...prev, [order.id]: e.target.value })); }}
                placeholder="Reason for rejection (required)..."
                className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-red-500 focus:border-transparent"
                rows={2}
                data-testid={`reject-notes-${order.id}`}
              />
            </div>
          ) : null;

          // ── THE THREE SECTIONS THAT CLOSED THE GAP WITH `/orders/:id` ────
          // (2026-08-10) Attachments + delivery paperwork, the read-only
          // approval stepper, and activity history. Rendered by BOTH panel
          // branches, because `OrderForm` has no counterpart for any of them —
          // see `components/OrderExpandedDetails.js`, which quotes the server
          // gate for each and explains why two of them are deliberately NOT
          // `canManageDepartmentOrders`.
          //
          // 🔑 THE STEPPER IS THE MOST VALUABLE OF THE THREE *ON THIS PAGE*:
          // every row here is an order awaiting the viewer's own approval, and
          // until now the panel showed no indication of which step it was on,
          // who had already approved, or who comes next. It renders READ-ONLY
          // (`showActions={false}`) — this page's own Approve/Reject stay the
          // only actions, because they honour the save-then-act contract via
          // `editorSaveRef` whereas `ApprovalStatus`'s own controls bypass the
          // form and end in `window.location.reload()`, which here would
          // discard the expanded row and any unsaved edit.
          //
          // 📦 `canUploadDelivery` — tranche 3 increment 3d migrated
          // `POST /api/orders/:id/files?category=delivery` off the any-department
          // `req.isBuyer` onto canManageDepartmentOrders in the ORDER'S OWN
          // department: `isGlobalAdmin || canManageDepartmentOrders(order's
          // dept) || isOwner` (+ isStores server-side). ⚠️ This page has no
          // `canManageStores` in scope and does not add one — a Stores user has
          // no reason to be on To Approve (the page lists orders where the
          // viewer is the pending APPROVER) — so the client gate is narrower
          // than the server's by that one term, which can only HIDE a control,
          // never offer one the server refuses. 🔑 The dept-manager term is
          // `canManageOrder(detail)` (per the order's own department), NOT the
          // unscoped `req.isBuyer`, so no new manifest read.
          // 🔻 `canReceiveItems` WAS RESOLVED HERE UNTIL 2026-08-15 and fed the
          // panel's retired "Items received" block; the received gate now lives
          // only where ticks can actually render (DepartmentOrders/MyOrders —
          // never here, since every row is `submitted`).
          const isOrderOwner = !!detail && currentUserId !== '' && String(detail.user_id) === String(currentUserId);
          const canUploadDelivery = !!detail && (isAdmin || canManageOrder(detail) || isOrderOwner);

          // Per FILE, mirroring `DELETE /api/orders/:id/files/:fileId`
          // (`routes/orders.js`). The moderator term is `canManageOrder(detail)`
          // — canManageDepartmentOrders resolved in the order's own department —
          // mirroring the server gate MIGRATED off the any-department
          // `req.isBuyer` in tranche 3 increment 3d. 🪤 `currentUserId` is a
          // STRING from localStorage while `uploaded_by_user_id` is a NUMBER — a
          // bare `===` is always false and the uploader branch never fires.
          const canRemoveOrderFile = (file) => {
            if (!file) return false;
            if (isAdmin || canManageOrder(detail)) return true;
            const uploader = file.uploaded_by_user_id;
            if (uploader != null && currentUserId !== '' && String(uploader) === String(currentUserId)) return true;
            return uploader == null && detail && detail.can_edit === true;
          };

          const expandedDetails = detail ? (
            <OrderExpandedDetails
              order={detail}
              canUploadDelivery={canUploadDelivery}
              canRemoveFile={canRemoveOrderFile}
              onFilesChanged={handleOrderFilesChanged}
              saveEpochRef={saveEpochRef}
              testIdPrefix={`approval-${order.id}`}
            />
          ) : null;

          // 🔀 (2026-08-17) — `adminNotesNode` for `OrderExpandedRow`. This
          // page has NO admin-notes EDITOR (unlike Department Orders' page-
          // level `editingNotes`/`savingNotes` buffer) — display-only,
          // exactly the shape DepartmentOrders' own display-only branch
          // uses, so the two pages agree on how the text reads. This is a
          // real gap CLOSED by the migration below: the old hand-rolled
          // panel never rendered `admin_notes` at all.
          // 🔻 It was PAIRED beside Expected Delivery in a narrow/wide row until
          // 2026-09-13; it now renders as the SECOND cell of Order Details row 2,
          // beside Expected Delivery, under PO Number, via `OrderForm`'s
          // `orderDetailsRow2Rest` slot (owner: *"Internal notes goes next to
          // Expected delivery date"*), and the label is `Internal notes`.
          // 🔑 The `detail.admin_notes ?` guard is load-bearing: the component
          // returns null for an empty note but the ELEMENT is truthy, which is
          // the phantom-child defect recorded in `OrderExpandedRow.js`.
          const adminNotesNode = detail && detail.admin_notes ? (
            <InternalNotesReadOnly notes={detail.admin_notes} />
          ) : null;

          // ════════════════════════════════════════════════════════════════
          // Expanded panel content — READ-ONLY BRANCH, MIGRATED (2026-08-17)
          // ONTO THE SHARED `components/OrderExpandedRow.js`, THE SAME
          // COMPONENT DEPARTMENT ORDERS / MY ORDERS RENDER.
          // ════════════════════════════════════════════════════════════════
          // 🔀 REPLACES the hand-rolled metadata grid + line-items table
          // this page carried alone (`git log` this file's history for the
          // deleted markup). Owner: *"I asked before for the departments
          // orders and my orders to look as similar as possible and share
          // code where possible."* An audit found this page's read-only
          // panel was the LONE divergence and the cause of every "not the
          // same" complaint — no "Order Details" header, rejection reason +
          // admin notes rendering NOWHERE, Cost Code/Payment Type shown
          // UNGATED (no `canViewAccounting`), an extra `#` index column, and
          // actions sitting beside the PO box instead of in the header.
          // Adopting `OrderExpandedRow` closes every one of those — see this
          // file's own docblock notes at the props below for each.
          //
          // 🚨 MOVING JSX GRANTS NOBODY ANYTHING; `OrderExpandedRow`
          // RESOLVES NOTHING. Every gate below is VERBATIM what the deleted
          // panel evaluated: `canManageOrder(detail)` gates the PO editor,
          // the line-item accounting selects and the item-actions menu,
          // exactly as it always did — only WHERE those controls render
          // changed, plus the accounting-columns NARROWING noted below.
          //
          // 🔀 **ACTION PLACEMENT SUPERSEDES THE 2026-08-08 "next to the PO
          // box, not on a row above" RULING (`pages/CLAUDE.md`, rewritten in
          // this same commit).** `quickActionsRow` now renders inside
          // `OrderForm`'s own "Order Details" header — the SAME slot the
          // EDIT branch below already feeds via `headerActions`, so the two
          // branches (and Dept Orders' identical header) no longer disagree
          // about where the group lives. The PO editor keeps its OWN
          // control (`OrderExpandedRow`'s `poEditorNode`, gated the same
          // `canManageOrder(detail)`) — it is no longer a sibling of the
          // buttons on one `flex-wrap` row, so its testid is renamed
          // `appr-po-number-${id}` (`testIdPrefix="appr"`, was
          // `approval-po-number-${id}`), and the `approval-po-row-${id}`
          // wrapper this page built around it no longer exists at all.
          //
          // 🔑 **THE REJECT-TEXTAREA DESIGN.** Reject is still two-step —
          // click Reject, the reason textarea appears, Confirm Reject
          // consumes it — and it MUST stay directly beneath the buttons
          // (the control that reveals it and the one that consumes it must
          // not be a panel's height apart). With the buttons now inside the
          // header bar, `rejectNotesField` goes to `OrderExpandedRow`'s
          // (additive, defaults null) `headerActionsBelow` prop, which
          // forwards it to `OrderForm`'s own `headerActionsBelow` — the SAME
          // slot the edit branch below already uses for this exact reason.
          // Dept Orders and My Orders pass neither prop, so they stay
          // BYTE-IDENTICAL through this change.
          //
          // 🔑 **`canViewAccounting` GATE — A NARROWING.** The deleted panel
          // showed Cost Code/Payment Type UNGATED whenever `canManageOrder`
          // was false (plain text, always visible). `OrderExpandedRow`
          // gates that plain-text display on `canViewAccounting` too — the
          // SAME term Dept Orders and My Orders already use. An approver who
          // lacks `canViewAccounting` now stops seeing those two columns;
          // nobody gains anything, only a narrower audience keeps seeing
          // what was already ungated. Flagged for staging — likely nobody in
          // practice, since accounting visibility and approval authority
          // usually travel together, but not verified against real data.
          //
          // 🚨 `#` INDEX COLUMN REMOVED — the shared component has none; it
          // was a bare `idx + 1` with no product meaning tests ever named.
          //
          // 🟡 A REAL, INTENDED SIDE EFFECT, same as Dept/My Orders' own
          // convergence: line items go from this page's own compact table +
          // `.md:hidden` mobile card pair to `OrderForm`'s ONE responsive,
          // collapsible item-card layout (`initialItemsCollapsed`). The
          // line-item testids this page built (`appr-item-flag-*`,
          // `appr-item-m-*`, `appr-item-*` for the notify menu) are GONE —
          // superseded by `OrderForm`'s own generic, non-prefixed testids
          // (`item-header-${index}`, …), exactly as already happened for
          // Dept Orders / My Orders.
          // 🔻 The per-ORDER `rowCostCodes` / `rowPaymentTypes` / `rowLoadFailures`
          // went with the All Productions mode on 2026-08-31. One department is
          // in view, so the page-level state below is this order's answer.
          // 🚨 `loadFailures` MUST keep travelling with the arrays it describes:
          // without it an emptied `costCodes` reaches `OrderForm` as "this
          // department genuinely has no cost codes" and renders a live select
          // holding only `-- None --`, so a coded line displays as UNCODED.

          const readOnlyExpandedContent = detail ? (
            <OrderExpandedRow
              order={order}
              detail={detail}
              showInlineEditor={false}
              canManage={canManageOrder(detail)}
              testIdPrefix="appr"
              formatMoney={formatMoney}
              costCodes={costCodes}
              paymentTypes={paymentTypes}
              onCostCodeChange={handleCostCodeChange}
              onPaymentTypeChange={handlePaymentTypeChange}
              onItemNotifyUpdated={handleItemNotifyUpdated}
              // 🔀 2026-10-03: these two were INLINE copies of the bodies the
              // two order-expansion hooks already had, which is how
              // `check-duplication` found them the moment the hooks' copies
              // moved into one place. Now the SAME `useExpandedOrderCore`
              // callbacks all three surfaces use — byte-identical bodies, so
              // nothing about this page's behaviour moved.
              onPoNumberSaved={handlePoNumberSaved}
              // Same shape as `onPoNumberSaved` above. Every order on this page
              // is `submitted` (`GET /api/approvals/pending` selects only
              // orders awaiting the viewer's approval), so
              // `EXPECTED_DELIVERY_EDITABLE_STATUSES` never matches here and
              // this wiring is inert today — included for parity with the
              // other two order pages rather than left to silently diverge if
              // a future change ever surfaces a later-stage order here.
              onExpectedDeliverySaved={handleExpectedDeliverySaved}
              // Every row on this page is `submitted` (`GET /api/approvals/pending`
              // selects only orders the viewer is the pending approver for) —
              // there is no delivery stage here for received ticks to apply to,
              // exactly as this page had none before.
              showReceivedTicks={false}
              adminNotesNode={adminNotesNode}
              quickActionsRow={approvalActionButtons}
              headerActionsBelow={rejectNotesField}
              canUploadDelivery={canUploadDelivery}
              canRemoveFile={canRemoveOrderFile}
              onFilesChanged={handleOrderFilesChanged}
              editor={{
                productions,
                suppliers,
                // Travels WITH the arrays it describes, so a page cannot hand
                // the form a list without saying whether it landed.
              loadFailures,
                gags,
                subDepartments,
                userAssignedProductions,
              }}
            />
          ) : null;

          // ── EXPAND-TO-EDIT (2026-08-08) ─────────────────────────────────
          // Product owner: *"expanding orders on the to approve page should
          // allow them to be edited"*, *"like on the department order page"*,
          // *"ok so make sure we allow edits on the to approve page please for
          // everything"*. And on why an APPROVER may edit at all: *"I dont mind
          // approvers being able to change the order as the log will say what
          // changed and the later approvers are more senior so they can overrule
          // the ones before."* — which is why editing here does NOT reset the
          // approval chain and does not warn; `helpers/orderEditDiff.js` writing
          // the field-level "what" into the audit log is the whole basis of that
          // ruling.
          //
          // 🔑 THE ONLY INPUT IS THE SERVER-COMPUTED `can_edit`, never a
          // client-side re-derivation. `=== true` rather than truthiness, so a
          // missing field on some future payload reads as "no", never as "yes".
          //
          // 🪤 …AND IT IS READ OFF `detail`, THE FRESH DETAIL PAYLOAD
          // (`GET /api/orders/:id`, `server/routes/orders.js:1104`), NOT off the
          // LIST row. The list row's copy goes stale within one session with no
          // second actor involved — `utils/refreshOneOrder.js` merges `status`,
          // `cancellation_reason`, `rejection_reason`, `admin_notes` and
          // `can_action` into the row after an action and never `can_edit` — so
          // gating on it opens an unlocked form for an order the `PUT` then
          // refuses. `!!detail` was already required to render this panel at
          // all, so the fresh flag costs nothing extra.
          //
          // (A `isTableLayout` term went with the order cards, 2026-10-18.)
          const showInlineEditor = !!detail && detail.can_edit === true;

          const expandedContent = showInlineEditor ? (
            <div>
              {/* 📍 **PLACEMENT — AND IT DEPARTS FROM THE OWNER'S "NEXT TO THE
                  PO BOX", KNOWINGLY.** His ruling was *"on to approve page put
                  'View Full Details / Reject / Approve' next to the PO box. not
                  on a row above"*, and it is honoured verbatim in the READ-ONLY
                  panel, where `InlinePoNumberEditor` is the panel's own
                  top-left element and the two sit on one `flex-wrap` row.
                  🪤 **In THIS branch the PO field is a field INSIDE
                  `components/OrderForm.js`**, several sections down a long
                  form. Putting the actions beside it would mean either
                  threading a node into a form shared by the create page and the
                  edit page, or re-rendering a SECOND PO control above the form
                  that can disagree with the one in it — and it would bury
                  Approve below the fold, which is the defect being fixed.
                  So they go at the TOP of the panel, which is where his
                  REASONING points even though his words do not: *"as the mouse
                  is close to that location as we've just expanded the row"* —
                  the user has just clicked the chevron in the first column, so
                  the panel's top-left is under the cursor. Flagged for him: a
                  wrong-but-visible button beats a correctly-placed absent one,
                  but he should know the two branches differ here.
                  🔀 **RESOLVED 2026-08-11 — THEY ARE IN `OrderForm`'s
                  `Order Details` HEADER NOW, WHICH IS THE FORM'S FIRST ELEMENT,
                  SO THE "TOP OF THE PANEL" PLACEMENT ABOVE IS UNCHANGED IN
                  SUBSTANCE AND THE BARE ROW IS GONE.** Product owner: *"lets try
                  moving the 'Reject Approve Actions' into the Order Details
                  header"*. His complaint was about **Department Orders** — *"it
                  matches to approve which looks ok as it has the two other
                  buttons with it but on department orders it just looks in the
                  wrong place"* — and this page moves with it purely so the two
                  edit branches do not disagree about where an order's actions
                  live. Nothing about the items, the gates, `saveThenRun` or the
                  two-step reject changed.
                  🔴 **THE READ-ONLY PANEL IS DELIBERATELY NOT TOUCHED, AND THAT
                  IS A RULING, NOT AN OVERSIGHT.** There the group sits beside
                  `InlinePoNumberEditor` because he asked for exactly that on
                  2026-08-08 — *"on to approve page put 'View Full Details /
                  Reject / Approve' next to the PO box. not on a row above"* —
                  and that panel has no `Order Details` header to move anything
                  into. Moving it would undo a verbatim ruling on a page he has
                  just said looks fine. */}
              <OrderInlineEditor
              order={detail}
              // The approval action group, rendered inside the form's
              // `Order Details` header. A finished, already-gated node —
              // visibility still depends on the approval chain and never on
              // `can_edit`, exactly as it did when this sat in a bare row.
              headerActions={approvalActionButtons}
              // 🚨 **THE REJECT REASON MUST TRAVEL WITH THE BUTTONS** — see its
              // own comment above: *"the control that REVEALS it and the control
              // that CONSUMES it must not be separated by a panel's height"*.
              // With the buttons in the header, leaving this here would have put
              // the revealed textarea ABOVE the button that revealed it and on
              // the far side of the whole form card. `headerActionsBelow`
              // renders it immediately under the header bar, inside the same
              // card, which keeps "directly beneath" literally true.
              headerActionsBelow={rejectNotesField}
              productions={productions}
              suppliers={suppliers}
              gags={gags}
              subDepartments={subDepartments}
              costCodes={costCodes}
              paymentTypes={paymentTypes}
              // 🚨 **UNCONDITIONAL SINCE 2026-08-31 — AND THIS IS THE ONE
              // BEHAVIOUR CHANGE IN THE ALL-MODE REMOVAL, KEPT DELIBERATELY.**
              // The prop was never passed here at all before `eb7c91e2`, and
              // that commit added it gated as
              // `isAllDepartments ? rowLoadFailures : undefined` — i.e. ALL
              // MODE ONLY, with single mode left on `OrderInlineEditor`'s `{}`
              // default (`components/OrderInlineEditor.js`) as a declared,
              // out-of-scope pre-existing gap. Deleting the mode as written
              // would have deleted the guard with it.
              //
              // 🔴 THAT GAP IS A LIVE `-- None --` MISREPORT AND IT IS
              // MEASURED, NOT ARGUED. Every row on this page is
              // `can_edit: true` (`GET /api/approvals/pending` returns only
              // orders awaiting the viewer), so in the TABLE layout this
              // direct `OrderInlineEditor` is the DOMINANT expanded branch.
              // With `loadFailures` defaulting to `{}`, a failed cost-code
              // fetch reaches `OrderForm` as "this department genuinely has
              // no cost codes" and renders a live, controlled `<select>` with
              // one option. Measured against this page on 2026-08-31, single
              // mode, an item stored with `cost_code_id: 42`:
              //   {"tag":"SELECT","value":"","selectedIndex":0,
              //    "options":["-- None --"],"notice":false}
              // A cost-coded line displayed as UNCODED — on a money field, on
              // the page where money is approved.
              //
              // 🔑 Passing the page-level value unconditionally is a NO-OP on
              // every healthy render (`loadFailures` is
              // `{costCodes: false, paymentTypes: false}` there, which the
              // `{}` default was already equivalent to). It differs ONLY on a
              // genuine fetch failure, which is exactly the case that was
              // wrong. Pinned by `Approvals.accountingLists.test.js` §4, whose
              // TABLE case was RED before this line changed.
              loadFailures={loadFailures}
              userAssignedProductions={userAssignedProductions}
              collapseRequestSignal={collapseRequestSignal}
              onCollapsePromptResolved={onPromptResolved}
              onDirtyChange={(isDirty) => handleEditorDirtyChange(order.id, isDirty)}
              // The id comes from the ROW, not from the saved payload the editor
              // hands back: the page has to invalidate a specific cache key, and
              // taking it from the PUT response would make the refresh depend on
              // the shape of a body we already know does not carry everything we
              // need (it has no `can_edit` at all).
              onSaved={() => handleEditorSaved(order.id)}
              onCancel={() => { setEditDirtyId(null); setExpandedOrders(new Set()); }}
              // The save handle behind `saveThenRun`. ONE ref for the page —
              // the table layout is an accordion, so only one editor is ever
              // mounted, and the component clears it on unmount.
              saveRequestRef={editorSaveRef}
              // Bumped by this editor on a successful save; read by
              // `expandedDetails`' `OrderExpandedDetails` below (same object)
              // so its upload-refresh GET can tell a stale response apart
              // from a current one. See the ref's own declaration above.
              saveEpochRef={saveEpochRef}
              testId={`approval-inline-editor-${order.id}`}
              />
              {/* 🔑 THE SAME NODE THE READ-ONLY BRANCH RENDERS, AND IT MUST
                  STAY IN BOTH — and on THIS page the editor branch is not the
                  minority case, it is EVERY row: `GET /api/approvals/pending`
                  selects the orders where the viewer IS the current pending
                  approver, so `can_edit` is true throughout and the read-only
                  branch is nearly dead code here. Omitting this would mean the
                  stepper, attachments and history never appeared on To Approve
                  at all — the exact shape of the 2026-08-09 defect where the
                  editor branch silently discarded the read-only one and the
                  owner found a panel with no Approve button. */}
              {expandedDetails}
            </div>
          ) : readOnlyExpandedContent;

          /* 📱 The PHONE table swaps the ROW as well as the column set — same
             testids, same `expandedContent`, no `actionable` (as the desktop
             row). No `onTitleClick`: on a phone the whole row expands. */
          if (isPhoneTable) {
            return (
              <OrderMobileTableRow
                key={order.id}
                order={order}
                isExpanded={isExpanded}
                isLoadingDetail={isLoadingDetail}
                onToggleExpand={toggleExpand}
                detail={detail}
                expandedContent={expandedContent}
                rowTestId={`approval-order-${order.id}`}
                toggleTestId={`approval-toggle-${order.id}`}
                detailsTestId={`approval-details-${order.id}`}
                chevronTestId={`approval-chevron-${order.id}`}
                testIdPrefix="approval"
              />
            );
          }
          return (
            <OrderTableRow
              key={order.id}
              order={order}
              isExpanded={isExpanded}
              isLoadingDetail={isLoadingDetail}
              onToggleExpand={toggleExpand}
              detail={detail}
              expandedContent={expandedContent}
              rowTestId={`approval-order-${order.id}`}
              toggleTestId={`approval-toggle-${order.id}`}
              detailsTestId={`approval-details-${order.id}`}
              chevronTestId={`approval-chevron-${order.id}`}
              testIdPrefix="approval"
            />
          );
        })}
      </OrderTableShell>

      {/* ══════════════════════════════════════════════════════════════════
          THE UNSAVED-CHANGES PROMPT (2026-08-10) — the twin of Department
          Orders'. Same dialog, same copy deck, same reasoning; only the
          reachable actions differ (Approve and Confirm Reject here — this page
          has no status lifecycle).
          ══════════════════════════════════════════════════════════════════
          Product owner: *"if there are unsaved changes and the user clicks
          approve lets have a popup saying 'un saved changes!' then offer
          buttons like 'save and approve' 'cancel' cancel doesnt undo the
          changes it just stops the approval"*.

          🚨 **A REFINEMENT OF HIS 2026-08-09 "approve butotn should approve and
          save the current state", NOT A REVERSAL.** The approval still applies
          to the SAVED order and a failed save still cannot approve. Do not
          restore the silent save.

          🔑 **`Keep editing` KEEPS THE EDITS AND DOES NOT APPROVE.** It clears
          the pending decision and nothing else — no discard, no collapse, no
          revert, no change to `editDirtyId`, and `rejectNotes` is untouched
          because it is page state and was never part of the save. His words:
          *"cancel doesnt undo the changes it just stops the approval"*. Pinned
          by `Approvals.unsavedActionPrompt.test.js` §3, which exists because
          "cancel" beside unsaved changes reads to a developer as "cancel my
          edits" and gets implemented that way.

          📝 The cancel slot is not labelled `Cancel`; the reasoning, and why it
          rests on a DIFFERENT argument from `pages/CLAUDE.md`'s "'Cancel'
          appears NOWHERE" rule, is at `constants/saveThenActPrompt.js`.

          🔴 **NO THIRD BUTTON** — "approve without saving" is deliberately not
          offered; approving a state different from the one on screen is what
          the 2026-08-09 ruling exists to prevent. */}
      <ConfirmDialog
        open={pendingSaveAction !== null}
        title={SAVE_THEN_ACT_TITLE}
        message={pendingSaveAction ? saveThenActCopy(pendingSaveAction.kind).message : null}
        cancelLabel={SAVE_THEN_ACT_KEEP_EDITING}
        confirmLabel={pendingSaveAction ? saveThenActCopy(pendingSaveAction.kind).confirmLabel : ''}
        initialFocus="cancel"
        onCancel={() => setPendingSaveAction(null)}
        onConfirm={() => {
          // Read BEFORE clearing — `setPendingSaveAction(null)` is what unmounts
          // the dialog, and the pending record is the only handle on the action.
          const pending = pendingSaveAction;
          setPendingSaveAction(null);
          if (pending) performSaveThenRun(pending);
        }}
      />
    </div>
  );
}

export default Approvals;
